import Env from './fw/env';
import FW from './fw/fw';
import Logger from './fw/logger';
import Dispatcher from './fw/dispatcher';
import Utils from './fw/utils';
import License from './fw/license';
import Autoplay from './fw/autoplay';
import LogsManagement from './fw/logs';
import LocalStorage from './fw/local-storage';
import DefaultsParam from './fw/defaults/input';
import InnerDefaults from './fw/defaults/inner';
import GoogleCast from './cast/cast';
import GoogleCastCordova from './cast/cordova-cast';
import GoogleAnalytics from './analytics/google-analytics';
import DomManagement from './core/dom';
import EventsManagement from './core/events';
import PlaybackManagement from './core/playback';
import InitManagement from './core/init';
import ReadyManagement from './core/ready';
import VolumeManagement from './core/volume';
import FullscreenManagement from './core/fullscreen';
import SeekManagement from './core/seek';
import ApiDestroy from './api/destroy';
import ApiSrc from './api/src';
import ModuleManagement from './modules/modules';
import Mux from './analytics/mux';
import Accessible from './accessibility/accessible';
import Chapters from './accessibility/chapters';
import VttJS from './accessibility/vtt-js';
import Thumbnails from './accessibility/thumbnails';
import UIManagement from './ui/ui';
import SizeManagement from './ui/resize';
import ColorsManagement from './ui/colors';
import Logo from './ui/logo';
import PosterManagement from './ui/poster';
import AdsHelpers from './ads/ads-helpers';
import AdBlocker from './ads/ad-blocker';

import '../less/rmp.less';


/**
 * The class to instantiate Radiant Media Player
 * @export
 * @class RadiantMP
*/
export default class RadiantMP {

  /**
   * @constructor
   * @param {string}  id - the id for the player container. This is a required parameter.
   */
  constructor(id) {

    // set inner defaults;
    InnerDefaults.set.call(this);

    // start Utils here
    this.rmpUtils = new Utils(this);
    this.rmpReadyManagement = new ReadyManagement(this);
    this.rmpSizeManagement = new SizeManagement(this);
    this.dom = {};
    this.id = id;

    // init window.rmpGlobals 
    Utils.initWindowRmpGlobals();

    // rmpInstances
    window.rmpGlobals.rmpInstances.push(this);
    if (typeof CustomEvent === 'function') {
      const event = new CustomEvent('rmpinstanceavailable', { detail: this });
      window.dispatchEvent(event);
    }
    // polyfillFullscreenEvents
    Utils.polyfillFullscreenEvents();

    // tcfapi
    AdsHelpers.tcfapi();

    // uspapi
    AdsHelpers.uspapi();


    this.dom.content = FW.createElement('div', 'rmp-content');

    // create basic manager instances
    this.rmpEventsManagement = new EventsManagement(this);
    this.rmpDomManagement = new DomManagement(this);
    this.rmpPlaybackManagement = new PlaybackManagement(this);
    this.rmpInitManagement = new InitManagement(this);
    this.rmpUIManagement = new UIManagement(this);
    this.rmpModuleManagement = new ModuleManagement(this);
    this.rmpLogsManagement = new LogsManagement(this);
    this.rmpAccessible = new Accessible(this);
    this.rmpSizeManagement = new SizeManagement(this);
    this.rmpColorsManagement = new ColorsManagement(this);
    this.rmpLicense = new License(this);
    this.rmpReadyManagement = new ReadyManagement(this);
    this.rmpVolumeManagement = new VolumeManagement(this);
    this.rmpFullscreenManagement = new FullscreenManagement(this);
    this.rmpSeekManagement = new SeekManagement(this);
    this.rmpPosterManagement = new PosterManagement(this);
    this.rmpLogo = new Logo(this);
    this.rmpAdsHelpers = new AdsHelpers(this);

    // detect mobile
    if ((navigator.userAgentData && navigator.userAgentData.mobile) || Env.isIos[0] || Env.isAndroid[0] ||
      Env.isIpadOS) {
      this.isMobile = true;
    } else {
      this.isMobile = false;
    }
  }

  /** 
   * @private
   * Reset internal status of Promises for player public API
   * @type {(type: string) => void} 
   */
  #resetPromise(type) {
    switch (type) {
      case 'fullscreen':
        this.fullscreenResolve = this.fullscreenReject = null;
        break;
      case 'init':
        this.initResolve = this.initReject = null;
        break;
      case 'play':
        this.playResolve = this.playReject = null;
        break;
      case 'setSrc':
        this.setSrcResolve = this.setSrcReject = null;
        break;
      case 'destroy':
        this.destroyResolve = this.destroyReject = null;
        break;
      default:
        break;
    }
  }

  /** 
   * Resolves stored promise based on type input
   * @type {(type: string) => void} 
   */
  resolvePromise(type) {
    if (this[type + 'Resolve']) {
      this[type + 'Resolve']();
      this.#resetPromise(type);
    }
  }

  /** 
   * Rejects stored promise based on type input
   * @type {(type: string) => void} 
   */
  rejectPromise(type, error) {
    Logger.print('info', `rejectPromise for ${type} API method with error code ${error.code || -1}`);
    if (this[type + 'Reject']) {
      this[type + 'Reject'](error);
      this.#resetPromise(type);
    }
  }

  /** 
   * Dispatch an event to the custom event system
   * @type {(eventName: string, data: object) => void} 
   */
  dispatch(eventName, data) {
    Logger.print('event', eventName);
    const event = this.events[eventName];
    if (event) {
      const validatedData = {
        type: eventName
      };
      if (data) {
        validatedData.data = data;
      }
      event.firePlayerEvent(validatedData);
    }
  }

  /** 
   * @private
   */
  #on(eventName, callback) {
    // First we grab the event from this.events
    let event = this.events[eventName];
    // If the event does not exist then we should create it!
    if (!event) {
      event = new Dispatcher(eventName);
      this.events[eventName] = event;
    }
    // Now we add the callback to the event
    event.registerCallback(callback);
  }

  /** 
   * Listen to an event from the custom event system
   * @type {(eventName: string, callback: function) => void} 
   */
  on(eventName, callback) {
    if (typeof eventName !== 'string' || eventName === '' || typeof callback !== 'function') {
      return;
    }
    const split = eventName.split(' ');
    split.forEach(eventItem => {
      this.#on(eventItem, callback);
    });
  }

  /*
   * @private
   */
  #one(eventName, callback) {
    const newCallback = (e) => {
      this.off(eventName, newCallback);
      callback(e);
    };
    this.on(eventName, newCallback);
  }

  /** 
   * Listen once to an event from the custom event system
   * @type {(eventName: string, callback: function) => void} 
   */
  one(eventName, callback) {
    if (typeof eventName !== 'string' || eventName === '' || typeof callback !== 'function') {
      return;
    }
    const split = eventName.split(' ');
    split.forEach(eventItem => {
      this.#one(eventItem, callback);
    });
  }

  /** 
   * @private
   */
  #off(eventName, callback) {
    // First get the correct event
    const event = this.events[eventName];
    // Check that the event exists and it has the callback registered
    if (event && event.callbacks.indexOf(callback) > -1) {
      // if it is registered then unregister it!
      event.unregisterCallback(callback);
      // if the event has no callbacks left, delete the event
      if (event.callbacks.length === 0) {
        delete this.events[eventName];
      }
    }
  }

  /** 
   * Unregister an event from the custom event system
   * @type {(eventName: string, callback: function) => void} 
   */
  off(eventName, callback) {
    if (typeof eventName !== 'string' || eventName === '' || typeof callback !== 'function') {
      return;
    }
    const split = eventName.split(' ');
    split.forEach(eventItem => {
      this.#off(eventItem, callback);
    });
  }

  /** 
   * @private
   */
  #analyticsPlay() {
    // if player not ready i.e. src changed or if linear ad on stage we do not log
    if (this.changingSrc || this.linearAdOnStage || this.paused) {
      return;
    }
    this.analyticsSecondsPlayed += 0.1;
  }

  /** 
   * @private
   */
  #prepOkStreamingForAppleDevices() {
    this.okHlsJS = false;
    if (this.hlsEngine === 'shakaplayer') {
      this.okShaka = false;
    }
    this.usingNativeHlsOnAppleDevices = true;
  }

  /** 
   * @private
   */
  #windowOnline() {
    Logger.print('info', 'online - network connection just became available');
    this.dispatch('online');
  }

  /** 
   * @private
   */
  #windowOffline() {
    Logger.print('info', 'offline - no network connection available');
    this.dispatch('offline');
  }

  /** 
   * @private
   */
  #preparePreload() {
    if (this.preload !== 'smart' && this.preload !== 'auto' && this.preload !== 'metadata') {
      this.preload = 'none';
    }
    const bandwidthData = window.rmpGlobals.rmpConnection.bandwidthData;
    if (this.preload === 'smart') {
      if (this.isMobile) {
        switch (bandwidthData.connectionType) {
          case 'cellular':
            this.preload = 'none';
            break;
          case 'wifi':
          case 'ethernet':
            this.preload = 'metadata';
            break;
          default:
            this.preload = 'none';
            break;
        }
      } else {
        // for desktop if bandwidthData.estimat > 1Mbps => metadata : none otherwise
        if (bandwidthData.estimate > -1) {
          if (bandwidthData.estimate > 1) {
            this.preload = 'metadata';
          } else {
            this.preload = 'none';
          }
        } else {
          this.preload = 'metadata';
        }
      }

    }

    if (this.shakaDrmEmeFairPlay) {
      this.forceMseHlsOnAppleDevices = true;
      if (this.preload !== 'none') {
        this.preload = 'auto';
      }
    }

    if (this.autoplay) {
      this.preload = 'auto';
    }

  }

  /** 
   * @private
   */
  #prepareGoogleCast() {
    if (this.googleCast && !window.rmpGlobals.googleCastInitialized) {
      window.rmpGlobals.googleCastInitialized = true;
      if (this.googleCastCordova) {
        Logger.print('google-cast-cordova', 'requesting Google Cast for Cordova support');
        this.rmpGoogleCast = new GoogleCastCordova(this);
        if (this.googleCastCordovaDebug) {
          this.rmpGoogleCast.loadDebug();
        } else {
          this.one('ready', this.rmpGoogleCast.init.bind(this.rmpGoogleCast));
        }
      } else {
        this.rmpGoogleCast = new GoogleCast(this);
        this.one('ready', this.rmpGoogleCast.init.bind(this.rmpGoogleCast));
      }
    }
  }

  /** 
 * @private
 */
  #prepareMuxData() {
    if ((typeof window.mux !== 'undefined' || typeof window.initShakaPlayerMux !== 'undefined') &&
      !FW.isEmptyObject(this.muxDataSettings) &&
      !FW.isEmptyObject(this.muxDataSettings.data) &&
      typeof this.muxDataSettings.data.env_key === 'string' &&
      this.muxDataSettings.data.env_key !== '') {
      this.rmpMux = new Mux(this);
      this.rmpMux.init();
    }
  }

  /**
   * @typedef {object} FlipVideo
   * @property {number} [x]
   * @property {number} [y]
   * @property {number} [z]
   * @typedef {object} CustomTrackNames
   * @property {string} [captions]
   * @property {string} [quality]
   * @property {string} [audio]
   * @typedef {object} CustomModule
   * @property {string} hint
   * @property {string} svg
   * @property {string} svgHover
   * @property {function} callback
   * @typedef {object} TextTrack
   * @property {string} lng
   * @property {string} name
   * @property {string} uri
   * @property {boolean} default
   * @typedef {object} TranscriptTrack
   * @property {string} lng
   * @property {string} name
   * @property {string} uri
   * @typedef {object} AdSchedule
   * @property {string} [preroll]
   * @property {string[][]} [midroll]
   * @property {string} [postroll]
   * @typedef {object} CuePoint
   * @property {number} time
   * @property {string} text
   * @typedef {object} AdsLabels
   * @property {string} [controlBarCustomMessage]
   * @property {string} [skipMessage]
   * @property {string} [textForClickUIOnMobile]
   * @typedef {object} ErrorLabels
   * @property {string} [customErrorMessage]
   * @property {string} [noSupportMessage]
   * @property {string} [noSupportInstallChrome]
   * @typedef {object} FccCaptionsLabels
   * @property {string} [fontColor]
   * @property {string} [fontOpacity]
   * @property {string} [fontSize]
   * @property {string} [fontFamily]
   * @property {string} [fontEdge]
   * @property {string} [backgroundColor]
   * @property {string} [backgroundOpacity]
   * @property {string} [windowColor]
   * @property {string} [windowOpacity]
   * @typedef {object} HintLabels
   * @property {string} [play]
   * @property {string} [pause]
   * @property {string} [seek]
   * @property {string} [enterFullscreen]
   * @property {string} [exitFullscreen]
   * @property {string} [quickRewind]
   * @property {string} [quickForward]
   * @property {string} [sharing]
   * @property {string} [sharingLink]
   * @property {string} [sharingEmbedCode]
   * @property {string} [quality]
   * @property {string} [volume]
   * @property {string} [mute]
   * @property {string} [unmute]
   * @property {string} [minus]
   * @property {string} [plus]
   * @property {string} [speed]
   * @property {string} [captions]
   * @property {string} [track]
   * @property {string} [transcript]
   * @property {string} [audio]
   * @property {string} [chapters]
   * @property {string} [live]
   * @property {string} [pip]
   * @property {string} [close]
   * @property {string} [settings]
   * @property {string} [back]
   * @property {string} [cast]
   * @property {string} [airplay]
   * @property {string} [playlist]
   * @property {string} [next]
   * @property {string} [previous]
   * @property {string} [related]
   * @property {string} [upNext]
   * @property {string} [up]
   * @property {string} [down]
   * @property {string} [left]
   * @property {string} [right]
   * @property {string} [default]
   * @property {string} [off]
   * @property {string} [loop]
   * @property {string} [info]
   * @typedef {object} Labels
   * @property {HintLabels} [hint]
   * @property {FccCaptionsLabels} [fccCaptions]
   * @property {ErrorLabels} [error]
   * @property {AdsLabels} [ads]
   * @typedef {object} Src
   * @property {string} [hls]
   * @property {string} [dash]
   * @property {string[]} [mp4]
   * @property {string[]} [webm]
   * @property {string} [fps]
   * @property {string} [m4a]
   * @property {string} [mp3]
   * @property {string} [ogg]
   * @typedef {object} FpsDrm
   * @property {string} certificatePath
   * @property {string} processSpcPath
   * @property {string} [licenseResponseType]
   * @property {object[]} [licenseRequestHeaders]
   * @property {object[]} [certificateRequestHeaders]
   * @property {function} [extractContentId]
   * @property {function} [licenseRequestMessage]
   * @property {function} [licenseRequestLoaded]
   * @property {string} [keySystem]
   * @typedef {object} ShakaDrm
   * @property {object} servers
   * @property {object} [clearKeys]
   * @property {object} [advanced]
   * @property {string} [minHdcpVersion]
   * @typedef {object} ShakaRestrictions
   * @property {number} [minWidth]
   * @property {number} [maxWidth]
   * @property {number} [minHeight]
   * @property {number} [maxHeight]
   * @property {number} [minPixels]
   * @property {number} [maxPixels]
   * @property {number} [minAudioBandwidth]
   * @property {number} [maxAudioBandwidth]
   * @property {number} [minVideoBandwidth]
   * @property {number} [maxVideoBandwidth]
   * @typedef {object} RetryParametersData
   * @property {number} [timeout]
   * @property {number} [maxAttempts]
   * @property {number} [delay]
   * @typedef {object} RetryParameters
   * @property {RetryParametersData} [manifest]
   * @property {RetryParametersData} [levels]
   * @property {RetryParametersData} [segment]
   * @property {RetryParametersData} [drm]
   * @typedef {object} Artwork
   * @property {string} src
   * @property {string} sizes
   * @property {string} type
   * @typedef {object} VideoPreference
   * @property {string} [label]
   * @property {string[]} [videoCodecs]
   * @property {string[]} [videoHdr]
   * @typedef {object} AudioPreference
   * @property {string} [lang]
   * @property {string} [label]
   * @property {string[]} [audioCodecs]
   * @property {boolean} [spatialAudio]
   * @typedef {object} SubtitlePreference
   * @property {string} [lang]
   * @property {string} [role]
   * @property {string[]} [textFormats]
   * @typedef {object} ContentMetadata
   * @property {string} [title]
   * @property {string} [description]
   * @property {string} [artist]
   * @property {string} [album]
   * @property {string} [id]
   * @property {string} [releaseDate]
   * @property {string} [thumbnail]
   * @property {string} [duration]
   * @property {string} [googleCastType]
   * @property {Artwork[]} [artwork]
   * @property {string[]} [poster]
   * @property {string[]} [endOfVideoPoster]
   * @property {string} [animatedPoster]
   * @typedef {object} RadiantMediaPlayerSettings
   * @property {string} licenseKey
   * @property {number} [width]
   * @property {number} [height]
   * @property {boolean} [iframeMode]
   * @property {boolean} [iframeAllowed]
   * @property {boolean} [autoHeightMode]
   * @property {number} [autoHeightModeRatio]
   * @property {string} [backgroundColor]
   * @property {string} [skin] 
   * @property {string} [iconsSet] 
   * @property {number} [iconsSize] 
   * @property {string} [customIconsLoc]
   * @property {string} [iconSpinner]
   * @property {boolean} [fullWindowSkin]
   * @property {string} [skinBackgroundColor]
   * @property {string} [skinButtonColor]
   * @property {string} [skinAccentColor]
   * @property {boolean} [hideControls]
   * @property {boolean} [hideCentralPlayButton]
   * @property {boolean} [hideCentralBuffering]
   * @property {boolean} [hideSeekBar]
   * @property {boolean} [hideFullscreen]
   * @property {boolean} [hideVolume]
   * @property {boolean} [hideExternalPlayerLabels]
   * @property {boolean} [disableKeyboardControl]
   * @property {boolean} [longFormContent]
   * @property {boolean} [automaticFullscreenOnLandscape]
   * @property {boolean} [automaticLandscapeOnFullScreen]
   * @property {boolean} [fullscreenFullWindowMode]
   * @property {string} [fixedLabelsLanguage]
   * @property {boolean} [doubleClickForFullscreen]
   * @property {boolean} [pauseContentWhenViewingAreaIsClicked]
   * @property {boolean} [autoplay]
   * @property {number} [autoplayCount]
   * @property {boolean} [requestAutoplayCapabilitiesData]
   * @property {number} [detectAutoplayTimeout]
   * @property {boolean} [viewableAutoplay]
   * @property {boolean} [viewablePlayPause]
   * @property {number} [viewableThreshold]
   * @property {boolean} [srcChangeAutoplay]
   * @property {string} [posterScaleMode]
   * @property {boolean} [autoplayAnimatedPoster]
   * @property {boolean} [hideInitialBlackFrameWhenNoPoster]
   * @property {string} [scaleMode]
   * @property {boolean} [loop]
   * @property {boolean} [muted]
   * @property {boolean} [permanentMuted]
   * @property {number} [initialVolume]
   * @property {boolean} [forceInitialVolume]
   * @property {boolean} [rememberVolume]
   * @property {boolean} [rememberCurrentTime]
   * @property {boolean} [rememberQuality]
   * @property {string} [bitrateDataDisplayed]
   * @property {boolean} [displayCodecData]
   * @property {string} [preload]
   * @property {number} [delayToFade]
   * @property {ContentMetadata} [contentMetadata]
   * @property {string} [appName]
   * @property {string} [logo]
   * @property {string} [logoLoc]
   * @property {boolean} [logoWatermark]
   * @property {string} [logoPosition]
   * @property {string} [logoMargin]
   * @property {boolean} [pauseContentOnLogoClick]
   * @property {boolean} [sharing]
   * @property {string} [sharingUrl]
   * @property {string} [sharingCode]
   * @property {string[]} [sharingNetworks]
   * @property {number} [frameRate]
   * @property {boolean} [strictGdprCompliance]
   * @property {boolean} [displayRemainingTimeInPlaceOfDuration]
   * @property {boolean} [errorOnlyShowCustomText]
   * @property {CustomTrackNames} [customTrackNames]
   * @property {FlipVideo} [flipVideo]
   * @property {boolean} [audioOnly]
   * @property {boolean} [audioOnlyUseVideoLayout]
   * @property {boolean} [audioOnlyIcecast]
   * @property {boolean} [audioOnlyID3UI]
   * @property {number} [debugLevel]
   * @property {number} [debugForceRawConsoleLogs]
   * @property {string} [pathToRmpFiles]
   * @property {number} [offsetStartPosition] 
   * @property {number} [quickRewind]
   * @property {number} [quickForward]
   * @property {boolean} [ajaxWithCredentials]
   * @property {boolean} [allowLocalStorage]
   * @property {boolean} [pip]
   * @property {boolean} [floating]
   * @property {number} [floatingThreshold]
   * @property {RetryParameters} [retryParameters]
   * @property {boolean} [dashFirst]
   * @property {ShakaRestrictions} [shakaRestrictions]
   * @property {ShakaDrm} [shakaDrm]
   * @property {object} [shakaRequestConfiguration]
   * @property {object} [shakaCustomConfig]
   * @property {object} [shakaFullCustomConfig]
   * @property {function|null} [shakaCustomRequestFilter]
   * @property {function|null} [shakaCustomResponseFilter]
   * @property {function|null} [shakaDrmFairPlayInitDataTransform]
   * @property {boolean} [shakaDrmEmeFairPlay]
   * @property {object} [shakaKeySystemsByURI]
   * @property {boolean} [shakaOffline]
   * @property {number} [shakaOfflinePreferredTrackQuality]
   * @property {number} [shakaMaxBufferAhead]
   * @property {number} [shakaMaxBufferBehind]
   * @property {boolean} [shakaClearBufferSwitch]
   * @property {boolean} [shakaLiveSync]
   * @property {boolean} [shakaAWSMediaTailor]
   * @property {object} [shakaAWSMediaTailorAdsParam]
   * @property {boolean} [ignoreHardwareResolution]
   * @property {boolean} [disableVideo]
   * @property {boolean} [disableAudio]
   * @property {boolean} [disableText]
   * @property {boolean} [disableIFrames]
   * @property {boolean} [disableThumbnails]
   * @property {boolean} [hlsJSFetchXhrWithCredentials]
   * @property {boolean} [autoBumpCaptionsOnVisibleControlBar]
   * @property {string} [hlsJSCaptionsTextTrack1Label]
   * @property {string} [hlsJSCaptionsTextTrack2Label]
   * @property {string} [hlsJSCaptionsTextTrack3Label]
   * @property {string} [hlsJSCaptionsTextTrack4Label]
   * @property {boolean} [hlsJSAppleAppStoreCompliance]
   * @property {boolean} [hlsJSStopDownloadWhilePaused]
   * @property {object} [hlsJSCustomConfig]
   * @property {object} [hlsJSFullCustomConfig]
   * @property {boolean} [forceNativeHlsOverHlsJS]
   * @property {boolean} [interstitialShowAdUI]
   * @property {boolean} [forceHlsJSOnAppleDevices]
   * @property {boolean} [forceMseHlsOnAppleDevices]
   * @property {boolean} [hlsJSProgressive]
   * @property {number} [hlsJSMaxBufferAhead]
   * @property {number} [hlsJSMaxBufferBehind]
   * @property {string} [manualSwitchingMode]
   * @property {boolean} [consumeLessEnergy]
   * @property {string} [hlsEngine]
   * @property {boolean} [capLevelToPlayerSize]
   * @property {boolean} [ignoreDevicePixelRatio]
   * @property {boolean} [lowLatencyMode]
   * @property {string[]} [preferredKeySystems]
   * @property {VideoPreference} [videoPreference]
   * @property {AudioPreference} [audioPreference]
   * @property {SubtitlePreference} [subtitlePreference]
   * @property {boolean} [enableCMCD]
   * @property {boolean} [useHeadersForCMCD]
   * @property {string} [cmcdSessionId]
   * @property {string} [cmcdContentId]
   * @property {number} [cmcdVersion]
   * @property {string[]} [cmcdIncludeKeys]
   * @property {boolean} [enableCMSD]
   * @property {FpsDrm} [fpsDrm]
   * @property {Src} src
   * @property {number} [initialQuality]
   * @property {boolean} [pdCheckCodecsDone]
   * @property {Labels} [labels]
   * @property {number} [dvrUIThreshold]
   * @property {TextTrack[]} [ccFiles]
   * @property {string} [crossorigin]
   * @property {string} [ccFontColor]
   * @property {number} [ccFontOpacity]
   * @property {number} [ccFontSize]
   * @property {string} [ccFontFamily]
   * @property {string} [ccFontEdge]
   * @property {string} [ccBackgroundColor]
   * @property {number} [ccBackgroundOpacity]
   * @property {string} [ccWindowColor]
   * @property {number} [ccWindowOpacity]
   * @property {TranscriptTrack[]} [transcripts]
   * @property {boolean} [enableGAVideoTracking]
   * @property {object} [gaEventParameters]
   * @property {string[]} [gaEvents]
   * @property {boolean} [enableMatomoTracking]
   * @property {string} [dataMatomoTitle]
   * @property {string} [seekBarThumbnailsLoc]
   * @property {string} [chaptersLoc]
   * @property {CuePoint[]} [cuePoints]
   * @property {boolean} [cuePointsVisibleOnTimeline]
   * @property {function|null} [cuePointsCallback]
   * @property {boolean} [speed]
   * @property {number[]} [speedRates]
   * @property {boolean} [googleCast]
   * @property {boolean} [googleCastCordova]
   * @property {boolean} [googleCastCordovaDebug]
   * @property {string} [googleCastReceiverAppId]
   * @property {boolean} [googleCastAndroidReceiverCompatible]
   * @property {string} [googleCastVmapAdsRequest]
   * @property {string} [googleCastAdTagUrl]
   * @property {object} [googleCastHeaders]
   * @property {boolean} [googleCastDrmToday]
   * @property {boolean} [googleCastEnableUITextDisplayer]
   * @property {object} [googleCastData]
   * @property {boolean} [googleCastDisableAds]
   * @property {boolean} [googleCastAllowSenderVolumeManagement]
   * @property {boolean} [airplay]
   * @property {boolean} [airplayWithMms]
   * @property {boolean} [ads]
   * @property {string} [adTagUrl]
   * @property {boolean} [adShowMarkers]
   * @property {boolean} [adOnAdBlockPreventPlayback]
   * @property {string} [adsResponse]
   * @property {boolean} [adImaTest]
   * @property {string[]} [adTagWaterfall]
   * @property {string} [adLocale]
   * @property {boolean} [adUseStyledNonLinearAds]
   * @property {AdSchedule} [adSchedule]
   * @property {string[]} [adScheduleWaterfall]
   * @property {function|null} [adScheduleCallback]
   * @property {boolean} [adScheduleReloadOnEnded]
   * @property {string} [adVpaidMode]
   * @property {boolean} [adVpaidControls]
   * @property {number} [adContentDuration]
   * @property {string} [adContentKeywords]
   * @property {string} [adContentTitle]
   * @property {boolean} [adForceNonLinearFullSlot]
   * @property {boolean} [adTagReloadOnEnded]
   * @property {boolean} [adShowRemainingTime]
   * @property {boolean} [adAutoAlign]
   * @property {boolean} [adEnablePreloading]
   * @property {boolean} [adCookiesEnabled]
   * @property {number} [adLiveStreamPrefetchSeconds]
   * @property {string} [adContinuousPlayback]
   * @property {boolean} [adForceNativeFullscreenOnIosForAds]
   * @property {number} [adNonLinearAdSlotWidth]
   * @property {number} [adNonLinearAdSlotHeight]
   * @property {string} [adCompanionBackfillMode]
   * @property {string} [adPpid]
   * @property {string} [adSessionId]
   * @property {object} [omidAccessModeRules]
   * @property {boolean} [adImaDai]
   * @property {string} [adImaDaiVodContentSourceId]
   * @property {string} [adImaDaiVodVideoId]
   * @property {boolean} [adImaDaiVodPod]
   * @property {function|null} [adImaDaiVodPodRequestStreamURLCallback]
   * @property {string} [adImaDaiLiveAssetKey]
   * @property {string} [adImaDaiPodNetworkCode]
   * @property {string} [adImaDaiCustomAssetKey]
   * @property {string} [adImaDaiApiKey]
   * @property {string} [adImaDaiBackupStream]
   * @property {object} [adImaDaiAdTagParameters]
   * @property {string} [adImaDaiStreamActivityMonitorId]
   * @property {string} [adImaDaiAuthToken]
   * @property {boolean} [adOutStream]
   * @property {boolean} [adOutStreamMutedAutoplay]
   * @property {string[]} [omidAllowedVendors]
   * @property {boolean} [adOmidSupport]
   * @property {boolean} [adAjaxWithCredentials]
   * @property {boolean} [adRmpVastEnableVpaid]
   * @property {boolean} [adSupportHlsCreatives]
   * @property {boolean} [adRmpVastEnableSimid]
   * @property {number} [adLoadMediaTimeout]
   * @property {number} [adLoadVastTimeout]
   * @property {number} [adMaxNumRedirects]
   * @property {boolean} [adCountDown]
   * @property {string} [adParser]
   * @property {string} [adPageUrl]
   * @property {string} [relatedLoc]
   * @property {object[]} [relatedData]
   * @property {number} [relatedUpNextOffset]
   * @property {boolean} [relatedUpNextAutoplay]
   * @property {boolean} [relatedEndedLoop]
   * @property {boolean} [relatedAudioOnly]
   * @property {function|null} [relatedCallback]
   * @property {string} [playlistLoc]
   * @property {object[]} [playlistData]
   * @property {boolean} [playlistUpNextAutoplay]
   * @property {boolean} [playlistEndedLoop]
   * @property {boolean} [playlistAudioOnly]
   * @property {function|null} [playlistCallback]
   * @property {boolean} [video360]
   * @property {number} [video360FocalLength]
   * @property {number} [video360MaxFocalLength]
   * @property {number} [video360MinFocalLength]
   * @property {number} [video360InitialLat]
   * @property {number} [video360InitialLon]
   * @property {number} [video360MobileGyroOffset]
   * @property {Src} [video360FallbackSrc]
   * @property {boolean} [loopModule]
   * @property {string} [infoModule]
   * @property {boolean} [theaterModeModule]
   * @property {CustomModule} [customModule]
   * @property {object} [muxDataSettings]
   * @property {boolean} [muxDataUseListData]
   * @property {object} [bitmovinAnalyticsConfig]
   * @property {string} [customAllowDenyListUrl]
   * @param {RadiantMediaPlayerSettings} settings
   * @param {object} [shadowRoot]
   * @return {Promise<>}
   */
  init(settings, shadowRoot) {
    this.refTime = FW.getNow();
    this.shadowRoot = shadowRoot;
    Logger.print(`info`, `init method starts at 0ms`);
    Logger.print('info', 'raw input settings', settings);

    return new Promise((resolve, reject) => {
      this.initResolve = resolve;
      this.initReject = reject;

      // init already called for this instance
      if (this.initAlreadyCalledForThisInstance) {
        this.rmpUtils.warning(1006);
        this.resolvePromise('init');
        return;
      }
      this.initAlreadyCalledForThisInstance = true;

      // invalid id
      if (typeof this.id !== 'string' || this.id === '') {
        this.rmpUtils.error(105, null, true);
        return;
      }

      // invalid container
      this.dom.container = document.getElementById(this.id);
      if (this.dom.container === null) {
        if (this.shadowRoot) {
          Logger.print('info', 'using shadowRoot', this.shadowRoot);
          this.dom.container = this.shadowRoot.querySelector(`#${this.id}`);
        } else {
          this.rmpUtils.error(105, null, true);
          return;
        }
      }
      Logger.print('info', 'player container', this.dom.container);

      // invalid parent for player container
      if (this.shadowRoot) {
        this.dom.containerParent = this.dom.container.getRootNode().host.parentNode;
      } else {
        this.dom.containerParent = this.dom.container.parentNode;
      }
      if (this.dom.containerParent === null) {
        this.rmpUtils.error(106, null, true);
        return;
      }
      Logger.print('info', 'parent to player container', this.dom.containerParent);

      // filter settings
      if (window.rmpGlobals.inputSettings) {
        window.rmpGlobals.inputSettings.push(settings);
      } else {
        window.rmpGlobals.inputSettings = [settings];
      }
      window.rmpGlobals.defaultInputSettings = DefaultsParam.input;

      DefaultsParam.filterInput.call(this, settings);
      // debugForceRawConsoleLogs
      if (this.debugForceRawConsoleLogs) {
        window.rmpGlobals.forceRawConsoleLogs = true;
      }

      // Add environment to rmpGlobals
      window.rmpGlobals.environment = this.environment;

      Logger.print(`info`, `player instance created with id ${this.id}`);
      Logger.print('info', 'submitted player settings follow', window.rmpGlobals.inputSettings);
      Logger.print('info', 'detected environment follows', window.rmpGlobals.environment);


      // retryParameters
      DefaultsParam.filterRetryParameters.call(this);
      // customTrackNames
      if (!FW.isEmptyObject(this.customTrackNames)) {
        if (!this.customTrackNames.captions) {
          this.customTrackNames.captions = [];
        }
        if (!this.customTrackNames.quality) {
          this.customTrackNames.quality = [];
        }
        if (!this.customTrackNames.audio) {
          this.customTrackNames.audio = [];
        }
      }

      this.#preparePreload();

      // api
      this.one('playing', () => {
        this.#analyticsPlay();
        this.analyticsInterval = window.setInterval(() => {
          this.#analyticsPlay();
        }, 100);
      });

      // GA
      if (this.enableGAVideoTracking) {
        this.rmpGoogleAnalytics = new GoogleAnalytics(this);
      }

      // support passing mp4/wbem as string
      if (typeof this.src.mp4 === 'string' && this.src.mp4 !== '') {
        this.src.mp4 = [this.src.mp4];
      }
      if (typeof this.src.webm === 'string' && this.src.webm !== '') {
        this.src.webm = [this.src.webm];
      }

      // check-contract
      if (this.licenseKey === '') {
        this.rmpLicense.terminate('no license key provided for Radiant Media Player - exit');
        return;
      }

      Logger.print('info', 'player version is ' + this.playerVersion);

      this.dom.container.classList.add('rmp-container', 'rmp-color-button', 'rmp-chrome');
      this.rmpUIManagement.showChrome();

      // filter pathToRmpFiles
      const lastChar = this.pathToRmpFiles.substr(this.pathToRmpFiles.length - 1);
      if (lastChar !== '/') {
        this.pathToRmpFiles = this.pathToRmpFiles + '/';
      }

      // validate contract
      if (!this.rmpLicense.validate()) {
        this.rmpLicense.terminate('license key check failed');
        return;
      }

      // check iframe
      if (Env.isInIframe) {
        Logger.print('info', 'player is loaded within an iframe');
        Logger.print('info', 'document.referrer - ' + document.referrer);
        Logger.print('info', 'document.location.href - ' + document.location.href);
        if (!this.iframeAllowed) {
          this.rmpLicense.terminate('iframe embedding not allowed');
          return;
        }
      }

      // online/offline
      this.windowOnlineFn = this.#windowOnline.bind(this);
      window.addEventListener('online', this.windowOnlineFn);

      this.windowOfflineFn = this.#windowOffline.bind(this);
      window.addEventListener('offline', this.windowOfflineFn);

      // Playback Capabilities 
      this.okShaka = Env.okShaka;
      this.okHls = Env.okHls;
      this.okHlsJS = Env.okHlsJS;
      if (this.forceNativeHlsOverHlsJS && this.okHlsJS && this.okHls) {
        this.okHlsJS = false;
      }

      if (this.forceMseHlsOnAppleDevices) {
        this.forceHlsJSOnAppleDevices = this.forceMseHlsOnAppleDevices;
      }

      if (Env.isIos[0]) {
        if (!this.forceHlsJSOnAppleDevices && (this.okHlsJS || (this.okShaka && this.hlsEngine === 'shakaplayer')) &&
          this.okHls) {
          this.#prepOkStreamingForAppleDevices();
        }
        // for ima we need setDisableCustomPlaybackForIOS10Plus = true so that ads does not play in content player for MMS
        if (this.ads && this.adParser === 'ima' && !this.usingNativeHlsOnAppleDevices) {
          this.adForceNativeFullscreenOnIosForAds = false;
        }
      }
      if (Env.isMacosSafari[0]) {
        if (!this.forceHlsJSOnAppleDevices && (this.okHlsJS || (this.okShaka && this.hlsEngine === 'shakaplayer')) &&
          this.okHls) {
          this.#prepOkStreamingForAppleDevices();
        }
      }
      if (Env.isIpadOS) {
        this.dom.container.classList.add('rmp-ipad-os');
        if (!this.forceHlsJSOnAppleDevices && (this.okHlsJS || (this.okShaka && this.hlsEngine === 'shakaplayer')) &&
          this.okHls) {
          this.#prepOkStreamingForAppleDevices();
        }
      }

      // 360 video do not work on macOS Safari and iPadOS Safari with MSE
      if (this.okHlsJS && this.okHls && this.video360 && Env.isApple) {
        this.okHlsJS = false;
        this.dashFirst = false;
      }

      this.okFps = Env.okFps;
      this.okWebM = Env.okWebM;
      this.okMp4 = Env.okMp4;
      if (this.audioOnly || this.playlistAudioOnly || this.relatedAudioOnly || this.audioOnlyUseVideoLayout) {
        this.okM4a = Env.okM4a;
        this.okMp3 = Env.okMp3;
        this.okOgg = Env.okOgg;
      }

      // audio-only
      if (this.audioOnly) {
        this.doubleClickForFullscreen = false;
        this.dom.container.style.backgroundColor = 'transparent';
        this.skin = 's1';
      } else if (this.backgroundColor !== '000000') {
        this.dom.container.style.backgroundColor = '#' + this.backgroundColor;
      }

      if (this.adOutStream) {
        this.ads = true;
        this.skin = 'outstream';
      }

      // prepare skin
      this.rmpUIManagement.prepareSkin();

      if (this.permanentMuted) {
        this.rememberVolume = false;
        this.pip = false;
        this.googleCast = false;
        this.airplay = false;
        this.muted = true;
      }

      if ((this.video360 && Env.ok360) || this.adOutStream) {
        this.doubleClickForFullscreen = false;
        this.pip = false;
        this.googleCast = false;
        this.airplay = false;
        this.capLevelToPlayerSize = false;
        this.ignoreDevicePixelRatio = false;
      }

      if (this.tvPlayerSkin) {
        this.pip = false;
        this.googleCast = false;
        this.airplay = false;
      }

      if (this.video360 && Env.ok360 && this.crossorigin === '') {
        this.crossorigin = 'anonymous';
      }

      if (typeof window.chrome === 'undefined') {
        this.googleCast = false;
      }

      // Google Cast
      this.#prepareGoogleCast();

      // MUX Data
      this.#prepareMuxData();

      if (this.audioOnly && this.audioOnlyUseVideoLayout) {
        this.dom.container.classList.add('rmp-audio-only-use-video-layout');
        this.audioOnly = false;
      }

      if (this.longFormContent) {
        this.shakaMaxBufferAhead = 120;
        this.hlsJSMaxBufferAhead = 120;
      }

      // autoplay
      // settings for detection
      let media = 'video';
      if (this.audioOnly) {
        media = 'audio';
        Logger.print('autoplay', 'testing audio tag with dummy MP3 blob');
      }
      let detectAutoplayTimeout = this.detectAutoplayTimeout;
      if (detectAutoplayTimeout <= 0) {
        detectAutoplayTimeout = 2500;
        if (this.isMobile) {
          detectAutoplayTimeout = 5000;
        }
      }
      // general adjustment of settings for autoplay
      if (this.viewablePlayPause) {
        this.viewableAutoplay = true;
      }
      if (this.autoplay || this.viewableAutoplay) {
        if (this.adOutStream && this.adOutStreamMutedAutoplay) {
          this.muted = true;
        }
        // rmpAutoplayCount
        if (LocalStorage.available(this.allowLocalStorage) && this.autoplayCount !== Infinity) {
          try {
            if (localStorage.getItem('rmpAutoplayCount') !== null) {
              let rmpAutoplayCount = JSON.parse(localStorage.getItem('rmpAutoplayCount'));
              Logger.print('autoplay', 'rmpAutoplayCount is ' + rmpAutoplayCount.value);
              if (rmpAutoplayCount && typeof rmpAutoplayCount.value === 'number' && rmpAutoplayCount.value <= 0) {
                this.autoplay = false;
              }
            }
          } catch (warning) {
            console.warn(warning);
          }
        }
        Autoplay.detect(media, detectAutoplayTimeout, this.muted).then(autoplayMode => {
          this.autoplayMode = autoplayMode;
          switch (this.autoplayMode) {
            case 'no-autoplay':
              this.rmpUtils.fireAutoplayFailure();
              break;
            case 'muted-autoplay':
              if (!this.muted) {
                this.muted = true;
              }
              break;
          }
          this.dispatch('autoplaymodedetected');
          Logger.print('autoplay', 'autoplayMode is ' + this.autoplayMode);
          this.rmpInitManagement.postAutoplayDetection();
        });
      } else if (this.requestAutoplayCapabilitiesData) {
        Autoplay.detect(media, detectAutoplayTimeout, this.muted).then(autoplayMode => {
          this.autoplayMode = autoplayMode;
          this.dispatch('autoplaymodedetected');
          Logger.print('autoplay', 'autoplayMode is ' + this.autoplayMode);
        });
        this.rmpInitManagement.postAutoplayDetection();
      } else {
        this.rmpInitManagement.postAutoplayDetection();
      }
    });
  }

  /******************************************* API *******************************************/
  /** 
   * Getter - the current readiness state of the player (true === player ready - false === player still preparing)
   * @type {() => boolean} 
   */
  get ready() {
    return this.playerReady;
  }

  /** 
   * Getter - the current initialized state of the player (true === player has been initialized e.g. viewer interaction or successful autoplay - false otherwise)
   * @type {() => boolean}
   */
  get playerInitialized() {
    if (this.rmpPlaybackManagement) {
      return this.rmpPlaybackManagement.playerInitialized;
    }
    return false;
  }

  /** 
   * Getter - the current environment the player is running in
   * @typedef {object} ScreenDimension
   * @property {number} width
   * @property {number} height
   * @typedef {object} ViewportSize
   * @property {number} width
   * @property {number} height
   * @typedef {object} SmartTVData
   * @property {boolean} tv
   * @property {boolean} webos
   * @property {boolean} tizen
   * @typedef {object} RadiantMediaPlayerEnvironment
   * @property {string} userAgent
   * @property {string} platform
   * @property {string} protocol
   * @property {boolean} primaryPointerIsTouchScreen
   * @property {[boolean, number]} isIos
   * @property {boolean} isIpadOS
   * @property {boolean} isMacOS
   * @property {[boolean, number]} isSafari
   * @property {[boolean, number]} isMacosSafari
   * @property {boolean} isApple
   * @property {[boolean, number]} isAndroid
   * @property {SmartTVData} smartTVData
   * @property {number} devicePixelRatio
   * @property {boolean} isStandalone
   * @property {boolean} isAndroidMobileOrTabletWebView
   * @property {boolean} okMp4
   * @property {boolean} okWebM
   * @property {boolean} okM4a
   * @property {boolean} okMp3
   * @property {boolean} okMse
   * @property {boolean} okMms
   * @property {boolean} okShaka
   * @property {boolean} okHlsJS
   * @property {boolean} okHls
   * @property {boolean} okEme
   * @property {boolean} okFps
   * @property {boolean} ok360
   * @property {boolean} isInIframe
   * @property {boolean} hasIntersectionObserver
   * @property {ViewportSize} viewportSize
   * @property {boolean} hasPassiveEventListeners
   * @property {ScreenDimension} screenDimension
   * @property {boolean} isOnline
   * @property {boolean} hasDownloadSupport
   * @property {boolean} hasNativeFullscreenSupport
   * @property {number} bwEstimate
   * @return {RadiantMediaPlayerEnvironment}
   */
  get environment() {
    return {
      userAgent: Env.userAgent,
      platform: Env.platform,
      protocol: Env.protocol,
      primaryPointerIsTouchScreen: Env.primaryPointerIsTouchScreen,
      isIos: Env.isIos,
      isIpadOS: Env.isIpadOS,
      isMacOS: Env.isMacOS,
      isSafari: Env.isSafari,
      isMacosSafari: Env.isMacosSafari,
      isApple: Env.isApple,
      isFirefox: Env.isFirefox,
      isAndroid: Env.isAndroid,
      smartTVData: Env.smartTVData,
      devicePixelRatio: Env.devicePixelRatio,
      isStandalone: Env.isStandalone,
      isAndroidMobileOrTabletWebView: Env.isAndroidMobileOrTabletWebView,
      okMp4: Env.okMp4,
      okWebM: Env.okWebM,
      okM4a: Env.okM4a,
      okMp3: Env.okMp3,
      okOgg: Env.okOgg,
      okMse: Env.okMse,
      okMms: Env.okMms,
      okShaka: Env.okShaka,
      okHlsJS: Env.okHlsJS,
      okHls: Env.okHls,
      okEme: Env.okEme,
      okFps: Env.okFps,
      ok360: Env.ok360,
      isInIframe: Env.isInIframe,
      hasIntersectionObserver: Env.hasIntersectionObserver,
      viewportSize: Env.viewportSize,
      hasPassiveEventListeners: Env.hasPassiveEventListeners,
      orientation: Env.orientation,
      hasScreenOrientationAPI: Env.hasScreenOrientationAPI,
      hasScreenOrientationLockAPI: Env.hasScreenOrientationLockAPI,
      screenDimension: Env.screenDimension,
      isOnline: Env.isOnline,
      hasDownloadSupport: Env.hasDownloadSupport,
      hasNativeFullscreenSupport: Env.hasNativeFullscreenSupport,
      bwEstimate: Env.getEstimateBW(false),
      isMobile: this.isMobile
    };
  }

  /** 
   * Getter - the player version (semantic versioning - example: 10.1.0)
   * @type {() => string} 
   */
  get playerVersion() {
    return this.innerPlayerVersion;
  }

  /** 
   * Getter - the current preload setting used by the player
   * @type {() => string} 
   */
  get preloadConfiguration() {
    return this.preload;
  }

  /** 
   * Getter - the current app name using the player
   * @type {() => string} 
   */
  get appNameConfiguration() {
    return this.appName;
  }

  /** 
   * Getter - the current paused state of the player (true === paused - false === playing)
   * @type {() => boolean} 
   */
  get paused() {
    if (this.rmpGoogleCast && this.rmpGoogleCast.isCastMediaLoaded()) {
      if (this.googleCastCordova) {
        if (this.castMedia.playerState === 'PAUSED') {
          return true;
        }
        return false;
      } else {
        return this.remotePlayer.isPaused;
      }
    } else if (this.dom && this.dom.video && typeof this.dom.video.paused === 'boolean') {
      return this.dom.video.paused;
    }
    return false;
  }

  /** 
   * Getter - the current stream type for the player (nosupport|dash|hls|mp4|webm|m4a|mp3|ogg|outstream)
   * @type {() => string} 
   */
  get streamType() {
    let streamType = 'nosupport';
    if (this.readingDashShaka) {
      streamType = 'dash';
    } else if (this.readingHlsJS || this.readingHls || this.readingHlsShaka) {
      streamType = 'hls';
    } else if (this.readingMp4 && !this.readingWebM) {
      streamType = 'mp4';
    } else if (this.readingMp4 && this.readingWebM) {
      streamType = 'webm';
    } else if (this.readingM4a) {
      streamType = 'm4a';
    } else if (this.readingMp3) {
      streamType = 'mp3';
    } else if (this.readingOgg) {
      streamType = 'ogg';
    } else if (this.readingOutstream) {
      streamType = 'outstream';
    }
    return streamType;
  }

  /** 
   * Play content. Returns Promise that resolves if play successful (rejects otherwise)
   * @return {Promise<>}
   */
  play() {
    Logger.print('api', 'play() called');
    return new Promise((resolve, reject) => {
      this.playResolve = resolve;
      this.playReject = reject;
      if (this.rmpGoogleCast && this.rmpGoogleCast.isCastMediaLoaded()) {
        if (this.googleCastCordova) {
          if (this.paused) {
            this.castMedia.play({});
            this.resolvePromise('play');
          }
        } else {
          if (this.remotePlayer.isPaused) {
            this.rmpGoogleCast.remotePlayerController.playOrPause();
            this.resolvePromise('play');
          }
        }
      } else if (this.linearAdOnStage && !this.adImaDai) {
        if (this.adPaused) {
          if (this.rmpAdsIma) {
            this.rmpAdsIma.resumeImaAds();
            this.resolvePromise('play');
          } else if (this.rmpAdsRmpVast) {
            this.rmpAdsRmpVast.resumeRmpVastAds();
            this.resolvePromise('play');
          }
        }
      } else if (this.paused) {
        this.initiatePlayback();
      }
    });
  }

  /** 
   * @type {() => void} 
   */
  pause() {
    Logger.print('api', 'pause() called');
    if (!this.playerReady) {
      this.rmpUtils.warning(1004);
      return;
    }
    if (this.rmpGoogleCast && this.rmpGoogleCast.isCastMediaLoaded()) {
      if (this.googleCastCordova) {
        if (!this.paused) {
          this.castMedia.pause({});
        }
      } else {
        if (!this.remotePlayer.isPaused) {
          this.rmpGoogleCast.remotePlayerController.playOrPause();
        }
      }
    } else if (this.linearAdOnStage && !this.adImaDai) {
      if (!this.adPaused) {
        if (this.rmpAdsIma) {
          this.rmpAdsIma.pauseImaAds();
        } else if (this.rmpAdsRmpVast) {
          this.rmpAdsRmpVast.pauseRmpVastAds();
        }
      }
    } else if (!this.paused) {
      this.initiatePlayback();
    }

  }

  /** 
   * @private
   */
  #postStop(resolve) {
    if (this.ads) {
      if (this.imaAdsManager) {
        this.off('adalladscompleted', this.postStopFn);
      } else if (this.rmpVast) {
        this.off('addestroyed', this.postStopFn);
      }
    }
    if (!this.paused) {
      this.pause();
    }
    if (this.isVod) {
      this.seekTo(0).catch(warning => {
        this.rmpUtils.warning(1016, warning);
      });
    }
    this.showPoster();
    this.dispatch('stopcompleted');
    resolve();
  }

  /** 
   * stop content. Returns Promise that resolves if stop successful (rejects otherwise)
   * @return {Promise<>}
   */
  stop() {
    return new Promise((resolve, reject) => {
      if (!this.playerReady) {
        this.rmpUtils.warning(1004);
        reject(this.warningData);
        return;
      }
      if (this.googleCastConnected) {
        this.pause();
        if (this.isVod) {
          this.seekTo(0).catch(warning => {
            this.rmpUtils.warning(1016, warning);
          });
          this.dom.current.style.width = 0;
          this.dom.handle.style.left = 0;
          FW.setText(this.dom.timeElapsedText, '00:00');
        }
        this.dispatch('stopcompleted');
        resolve();
        return;
      }

      this.postStopFn = this.#postStop.bind(this, resolve);

      if (this.adOnStage) {
        if (this.imaAdsManager) {
          if (this.adLinear) {
            this.on('adalladscompleted', this.postStopFn);
            this.stopAds().catch(warning => {
              this.rmpUtils.warning(1021, warning);
              this.postStopFn();
            });
          } else {
            this.stopAds().catch(warning => {
              this.rmpUtils.warning(1021, warning);
            });
            this.postStopFn();
          }
        } else if (this.rmpVast) {
          this.on('addestroyed', this.postStopFn);
          this.stopAds().catch(warning => {
            this.rmpUtils.warning(1021, warning);
            this.postStopFn();
          });
        }
      } else {
        this.postStopFn();
      }
    });
  }

  /** 
   * @type {() => void} 
   */
  fastForward() {
    if (this.rmpQuickSeekManagement) {
      this.rmpQuickSeekManagement.fastForwardClick(null);
    }
  }

  /** 
   * @type {() => void} 
   */
  fastRewind() {
    if (this.rmpQuickSeekManagement) {
      this.rmpQuickSeekManagement.fastRewindClick(null);
    }
  }

  /** 
   * Returns Promise that resolves to a string ('livedvr'|'voddvr'|'live'|'vod') representing the current stream mode. Note that this method never rejects.
   * @return {Promise<string>}
   */
  getStreamMode() {
    return new Promise(resolve => {
      if (!this.detectLive) {
        if (this.isLive) {
          resolve('live');
        } else if (this.isDvr) {
          if (this.isDvrLiveMode) {
            resolve('livedvr');
            return;
          }
          resolve('voddvr');
        }
        resolve('vod');
      } else {
        this.one('durationchange', () => {
          if (this.isLive) {
            resolve('live');
          } else if (this.isDvr) {
            if (this.isDvrLiveMode) {
              resolve('livedvr');
              return;
            }
            resolve('voddvr');
          }
          resolve('vod');
        });
      }
    });
  }

  /** 
   * @type {() => object} 
   */
  get shakaPlayerInstance() {
    if (!this.shakaPlayer) {
      return {};
    }
    return this.shakaPlayer;
  }

  /** 
   * @type {() => object} 
   */
  get htmlMediaElement() {
    if (this.dom.video) {
      return this.dom.video;
    }
    return {};
  }

  /** 
   * @type {() => number} 
   */
  get bandwidthEstimate() {
    let bandwidthEstimate = -1;
    if (this.hlsJS) {
      if (!Number.isNaN(this.hlsJS.bandwidthEstimate)) {
        bandwidthEstimate = Math.round(this.hlsJS.bandwidthEstimate);
      }
    } else if (this.shakaPlayer) {
      if (typeof this.shakaPlayer.getStats === 'function') {
        bandwidthEstimate = this.shakaPlayer.getStats();
        if (bandwidthEstimate && FW.isFiniteNumber(bandwidthEstimate.estimatedBandwidth)) {
          bandwidthEstimate = Math.round(bandwidthEstimate.estimatedBandwidth);
        } else {
          bandwidthEstimate = -1;
        }
      }
    }
    if (bandwidthEstimate <= 0) {
      bandwidthEstimate = Env.getEstimateBW(false);
    }
    return bandwidthEstimate;
  }

  /******************************************* Buffer API *******************************************/
  /** 
  * @private
  */
  #getBufferLength(type) {
    if (this.rmpGoogleCast && this.rmpGoogleCast.isCastMediaLoaded()) {
      return -1;
    }
    if (this.linearAdOnStage && !this.adImaDai && this.usingNativeHlsOnAppleDevices) {
      return -1;
    }
    if (this.dom && this.dom.video) {
      const video = this.dom.video;
      let currentTime = FW.getMediaCurrentTime(video);
      if (currentTime < 0) {
        return -1;
      }
      currentTime = currentTime * 1000;
      if (typeof video.buffered !== 'undefined' &&
        typeof video.buffered.start === 'function' &&
        typeof video.buffered.end === 'function') {
        const buffered = video.buffered;
        for (let i = 0; i < buffered.length; ++i) {
          const start = buffered.start(i) * 1000;
          const end = buffered.end(i) * 1000;
          if (start <= currentTime && end >= currentTime) {
            if (type === 'behind') {
              return currentTime - start;
            } else {
              return end - currentTime;
            }
          }
        }
      }
    }
    return -1;
  }

  /** 
   * @type {() => number} 
   */
  get bufferAhead() {
    return this.#getBufferLength('ahead');
  }

  /** 
   * @type {() => number} 
   */
  get bufferBehind() {
    return this.#getBufferLength('behind');
  }

  /******************************************* Loop API *******************************************/
  /** 
   * @type {() => boolean} 
   */
  get loopConfiguration() {
    return this.loop;
  }

  /** 
   * @type {(loop: boolean) => void} 
   */
  set loopConfiguration(loop) {
    if (!this.isVod || (this.ads && Env.isIos[0])) {
      Logger.print('api', 'cannot set loop for live or DVR streams or player loaded with ads on iOS');
      this.loop = false;
      return;
    }
    if (typeof loop === 'boolean' && this.dom.video) {
      this.dom.video.loop = this.loop = loop;
    }
  }

  /******************************************* Controls API *******************************************/
  /** 
   * @type {() => boolean} 
   */
  get controls() {
    return this.innerControls;
  }

  /** 
   * @type {(inputControls: boolean) => void} 
   */
  set controls(inputControls) {
    if (typeof inputControls !== 'boolean') {
      this.rmpUtils.warning(1003);
      return;
    }
    if (this.rmpUIManagement) {
      if (inputControls) {
        this.rmpUIManagement.showControlsUI();
      } else {
        this.rmpUIManagement.hideControlsUI();
      }
    }
  }

  /** 
   * @type {() => boolean} 
   */
  get controlsVisible() {
    if (!this.hideControls && this.dom.container) {
      if (this.dom.container.classList.contains('rmp-chrome')) {
        return true;
      } else if (this.dom.container.classList.contains('rmp-no-chrome')) {
        return false;
      }
    }
    return false;
  }

  /** 
   * @type {(input: boolean) => void} 
   */
  set controlsVisible(input) {
    if (typeof input !== 'boolean') {
      this.rmpUtils.warning(1003);
      return;
    }
    if (!this.hideControls && this.rmpUIManagement) {
      if (input) {
        this.rmpUIManagement.showChrome();
      } else {
        this.rmpUIManagement.hideChrome();
      }
    }
  }

  /******************************************* Current time/duration API *******************************************/
  /** 
   * @private
   */
  #getCastCurrentTime() {
    if (this.googleCast) {
      if (this.googleCastCordova) {
        if (this.castMedia) {
          const currentTime = this.castMedia.getEstimatedTime();
          if (FW.isFiniteNumber(currentTime) && currentTime >= 0) {
            return currentTime * 1000;
          }
        }
      } else if (this.remotePlayer && FW.isFiniteNumber(this.remotePlayer.currentTime) && this.remotePlayer.currentTime >= 0) {
        return this.remotePlayer.currentTime * 1000;
      }
    }
    return -1;
  }

  /** 
   * @type {() => number} 
   */
  get currentTime() {

    if (this.rmpGoogleCast && this.rmpGoogleCast.isCastMediaLoaded()) {
      return this.#getCastCurrentTime();
    }
    if (this.linearAdOnStage && !this.adImaDai && this.usingNativeHlsOnAppleDevices && this.rmpEventsManagement) {
      if (this.isLive) {
        return -1;
      }
      return this.rmpEventsManagement.currentTimeBeforeAd;
    }
    if (this.dom && this.dom.video) {
      const video = this.dom.video;
      if (this.isDvr) {
        if (this.isDvrLiveMode) {
          if (this.readingHlsJS) {
            return video.currentTime * 1000;
          } else if (this.readingShaka) {
            const seekRange = this.shakaPlayer.seekRange();
            const start = seekRange.start;
            return (video.currentTime - start) * 1000;
          }
        } else {
          if (this.rmpEventsManagement && this.rmpEventsManagement.dvrCurrentTime >= 0) {
            return this.rmpEventsManagement.dvrCurrentTime * 1000;
          } else {
            return -1;
          }
        }
      }
      const currentTime = FW.getMediaCurrentTime(video);
      if (currentTime >= 0) {
        return currentTime * 1000;
      }
    }
    return -1;
  }

  /** 
   * @type {() => number} 
   */
  get currentTimeBeforeSeek() {
    if (this.rmpEventsManagement) {
      return this.rmpEventsManagement.currentTimeBeforeSeek;
    }
    return -1;
  }

  /** 
   * seek into content. Returns Promise that resolves if seekTo successful (rejects otherwise).
   * @param {number} msSeek - time in milliseconds to seek into content
   * @return {Promise<>}
   */
  seekTo(msSeek) {
    return new Promise((resolve, reject) => {
      if (!FW.isFiniteNumber(msSeek) || msSeek < 0) {
        this.rmpUtils.warning(1003);
        reject(this.warningData);
        return;
      }
      if (this.isLive) {
        this.rmpUtils.warning(1031);
        reject(this.warningData);
        return;
      }
      if (this.isDvr) {
        this.rmpUtils.warning(1033);
        reject(this.warningData);
        return;
      }
      if (this.linearAdOnStage) {
        this.rmpUtils.warning(1032);
        reject(this.warningData);
        return;
      }
      if (this.dom && this.dom.video) {
        const video = this.dom.video;
        const seekValue = msSeek / 1000;
        Logger.print('api', 'seekTo requested at ' + msSeek + ' ms');
        if (this.rmpGoogleCast && this.rmpGoogleCast.isCastMediaLoaded()) {
          if (this.googleCastCordova) {
            const seekRequest = new window.chrome.cast.media.SeekRequest();
            seekRequest.currentTime = seekValue;
            this.castMedia.seek(seekRequest);
            resolve();
            return;
          } else {
            this.remotePlayer.currentTime = seekValue;
            this.rmpGoogleCast.remotePlayerController.seek();
            resolve();
            return;
          }
        }
        video.addEventListener('seeked', () => {
          resolve();
        }, { once: true });
        try {
          video.currentTime = seekValue;
        } catch (error) {
          this.rmpUtils.warning(1034, error);
          reject(this.warningData);
        }
      } else {
        this.rmpUtils.warning(1004);
        reject(this.warningData);
      }
    });
  }

  /** 
   * @private
   */
  #getCastDuration() {
    if (this.googleCast) {
      if (this.googleCastCordova) {
        if (this.castMedia && this.castMedia.media) {
          if (FW.isInfinityNumber(this.castMedia.media.duration)) {
            return Number.POSITIVE_INFINITY;
          } else {
            const duration = this.castMedia.media.duration;
            if (duration >= 0) {
              return duration * 1000;
            }
          }
        }
      } else {
        if (this.remotePlayer) {
          if (FW.isInfinityNumber(this.remotePlayer.duration)) {
            return Number.POSITIVE_INFINITY;
          }
          let duration;
          if (FW.isFiniteNumber(this.remotePlayer.duration) && this.remotePlayer.duration >= 0) {
            duration = this.remotePlayer.duration;
          }
          if (duration) {
            return duration * 1000;
          }
        }
      }
    }
    return -1;
  }

  /** 
   * @type {() => number} 
   */
  get duration() {
    if (this.rmpGoogleCast && this.rmpGoogleCast.isCastMediaLoaded()) {
      return this.#getCastDuration();
    }
    if (this.linearAdOnStage && !this.adImaDai && this.usingNativeHlsOnAppleDevices) {
      return -1;
    }
    if (this.dom && this.dom.video) {
      const video = this.dom.video;
      if (this.isLive) {
        if (FW.isFiniteNumber(video.duration)) {
          return video.duration * 1000;
        } else if (FW.isInfinityNumber(video.duration)) {
          return Number.POSITIVE_INFINITY;
        }
      }
      if (this.isDvr) {
        if (this.rmpHlsJS && this.rmpHlsJS.hlsJSLevelDuration >= 0) {
          return this.rmpHlsJS.hlsJSLevelDuration * 1000;
        } else if (this.shakaPlayer) {
          const seekRange = this.shakaPlayer.seekRange();
          const start = seekRange.start;
          const end = seekRange.end;
          if (FW.isFiniteNumber(end) && FW.isFiniteNumber(start) && end > 0 && start >= 0) {
            const duration = end - start;
            return duration * 1000;
          }
        }
        return -1;
      }
      const duration = FW.getMediaDuration(video);
      if (duration >= 0) {
        return duration * 1000;
      }
    }
    return -1;
  }

  /******************************************* Fullscreen API *******************************************/
  /** 
   * @type {() => boolean} 
   */
  getFullscreen() {
    return this.isInFullscreen;
  }

  /** 
   * enter/exit fullscreen based on input parameter. Returns Promise that resolves if setFullscreen successful (rejects otherwise)
   * @param {boolean} enterFullscreen - the new fullscreen state 
   * @return {Promise<>}
   */
  setFullscreen(enterFullscreen) {
    return new Promise((resolve, reject) => {
      this.fullscreenResolve = resolve;
      this.fullscreenReject = reject;
      if (typeof enterFullscreen === 'boolean') {
        if ((this.isInFullscreen && enterFullscreen) || (!this.isInFullscreen && !enterFullscreen)) {
          if (this.isInFullscreen) {
            this.rmpUtils.warning(1022);
          } else {
            this.rmpUtils.warning(1023);
          }
          this.rejectPromise('fullscreen', this.warningData);
        } else if (this.rmpFullscreenManagement) {
          this.rmpFullscreenManagement.executeFullscreenRequest();
        }
      }
    });
  }

  /******************************************* Live/DVR API *******************************************/
  /** 
   * @type {() => number} 
   */
  get livePresentationStartTimeAsDate() {
    if (this.shakaPlayer) {
      const presentationStartTimeAsDate = this.shakaPlayer.getPresentationStartTimeAsDate();
      if (presentationStartTimeAsDate) {
        return presentationStartTimeAsDate;
      }
    } else if (this.rmpHlsJS) {
      return this.rmpHlsJS.rawProgramDateTime;
    } else if (this.dom.video && typeof this.dom.video.getStartDate === 'function') {
      // Apple's native HLS gives us getStartDate(), which is only available if
      // EXT-X-PROGRAM-DATETIME is in the playlist.
      const startDate = this.dom.video.getStartDate();
      if (startDate && typeof startDate.getTime === 'function' && Number.isNaN(startDate.getTime())) {
        return -1;
      }
      return startDate;
    }
    return -1;
  }

  /** 
   * seek into a DVR stream (recorded part). Returns Promise that resolves if dvrSeekTo successful (rejects otherwise)
   * @param {number} msSeek - time in milliseconds to seek into DVR content
   * @return {Promise<>}
   */
  dvrSeekTo(msSeek) {
    return new Promise((resolve, reject) => {
      if (!FW.isFiniteNumber(msSeek) || msSeek < 0) {
        this.rmpUtils.warning(1003);
        reject(this.warningData);
        return;
      }
      if (!this.isDvr) {
        this.rmpUtils.warning(1035);
        reject(this.warningData);
        return;
      }
      if (this.linearAdOnStage) {
        this.rmpUtils.warning(1036);
        reject(this.warningData);
        return;
      }
      Logger.print('api', 'dvrSeekTo requested at (ms) ' + msSeek);
      const nowDuration = this.duration;
      if (nowDuration > 0 && this.rmpSeekManagement && this.dom && this.dom.video) {
        const percent = msSeek / nowDuration;
        Logger.print('api', 'percent dvrSeekTo ' + percent + '%');
        if (percent >= 1) {
          this.rmpUtils.warning(1003);
          reject(this.warningData);
          return;
        }
        if (this.googleCastConnected) {
          window.setTimeout(() => {
            resolve();
          }, 1);
        } else {
          this.dom.video.addEventListener('seeked', () => {
            resolve();
          }, { once: true });
        }
        this.rmpSeekManagement.executeSeek(percent);
      } else {
        this.rmpUtils.warning(1038);
        reject(this.warningData);
      }

    });
  }

  /** 
   * sync a live or DVR (live part) stream with the current edge of live stream. Returns Promise that resolves if syncToLiveEdge successful (rejects otherwise)
   * @return {Promise<>}
   */
  syncToLiveEdge() {
    return new Promise((resolve, reject) => {
      if (this.linearAdOnStage) {
        this.rmpUtils.warning(1037);
        reject(this.warningData);
        return;
      }
      if (this.dom && this.dom.video) {
        const video = this.dom.video;
        if (this.isLive) {
          video.addEventListener('seeked', () => {
            resolve();
          }, { once: true });
          if (this.rmpHlsJS) {
            this.rmpHlsJS.syncHlsJSToLiveEdge();
          } else if (this.shakaPlayer) {
            this.shakaPlayer.goToLive();
          }
        } else if (this.isDvr && this.rmpDvrManagement) {
          if (this.isDvrLiveMode) {
            resolve();
          } else {
            video.addEventListener('seeked', () => {
              resolve();
            }, { once: true });
          }
          this.rmpDvrManagement.setLiveMode();
        }
      } else {
        this.rmpUtils.warning(1004);
        reject(this.warningData);
      }
    });
  }

  /******************************************* Volume API *******************************************/
  /** 
   * @private
   */
  #getVolumeAds() {
    if (this.imaAdsManager) {
      return this.imaAdsManager.getVolume();
    } else if (this.rmpVast) {
      return this.rmpVast.volume;
    }
    return -2;
  }

  /** 
   * Get the current player volume
   * @type {() => number} 
   */
  get volume() {
    let volume;
    if (this.rmpGoogleCast && this.rmpGoogleCast.isCastMediaLoaded()) {
      if (this.googleCastCordova) {
        volume = this.castMedia.volume.level;
      } else {
        volume = this.remotePlayer.volumeLevel;
      }
    } else {
      if (this.linearAdOnStage && !this.adImaDai) {
        volume = this.#getVolumeAds();
      } else if (this.dom && this.dom.video) {
        try {
          volume = this.dom.video.volume;
        } catch (warning) {
          console.warn(warning);
          volume = -1;
        }
      }
    }
    if (FW.isFiniteNumber(volume) && volume !== -2) {
      return volume;
    }
    return -1;
  }

  /** 
   * Set the current player volume (parameter value between 0 and 1)
   * @type {(inputVolume: number) => void} 
   */
  set volume(inputVolume) {
    if (!FW.isFiniteNumber(inputVolume)) {
      this.rmpUtils.warning(1003);
      return;
    }
    if (!this.playerReady) {
      this.rmpUtils.warning(1004);
      return;
    }
    let newVolume;
    if (inputVolume < 0) {
      newVolume = 0;
    } else if (inputVolume > 1) {
      newVolume = 1;
    } else {
      newVolume = inputVolume;
    }
    // we unmute in case player is muted
    if (this.mute) {
      this.mute = false;
    }
    let currentVolume = this.volume;
    if (currentVolume >= 0 && currentVolume <= 1) {
      if (currentVolume === newVolume) {
        Logger.print('api', 'current volume is same as input volume - exit');
        return;
      }
    }
    if (this.rmpGoogleCast && this.rmpGoogleCast.isCastMediaLoaded()) {
      if (this.googleCastCordova) {
        const volume = new window.chrome.cast.Volume(newVolume, this.mute);
        const volumeRequest = new window.chrome.cast.media.VolumeRequest(volume);
        this.castMedia.setVolume(volumeRequest);
      } else {
        this.remotePlayer.volumeLevel = newVolume;
        this.rmpGoogleCast.remotePlayerController.setVolumeLevel();
      }
    } else {
      try {
        this.dom.video.volume = newVolume;
      } catch (warning) {
        console.warn(warning);
      }
      if (this.linearAdOnStage && !this.adImaDai) {
        this.rmpAdsHelpers.setAdVolume(newVolume);
      }
    }
    Logger.print('api', 'set volume to ' + newVolume);

  }

  /** 
   * Get the current player mute state 
   * @type {() => boolean} 
   */
  get mute() {
    return this.muted;
  }

  /** 
   * @private
   */
  #mute() {
    this.volumeBeforeMute = this.volume;
    if (this.volumeBeforeMute < 0 || this.volumeBeforeMute > 1) {
      this.volumeBeforeMute = 0.5;
    }
    this.muted = true;
    try {
      this.dom.video.muted = true;
    } catch (warning) {
      console.warn(warning);
    }
    if (this.rmpGoogleCast && this.rmpGoogleCast.isCastMediaLoaded()) {
      if (this.googleCastCordova) {
        const volume = new window.chrome.cast.Volume(this.volumeBeforeMute, true);
        const volumeRequest = new window.chrome.cast.media.VolumeRequest(volume);
        this.castMedia.setVolume(volumeRequest);
      } else {
        if (!this.remotePlayer.isMuted) {
          this.rmpGoogleCast.remotePlayerController.muteOrUnmute();
        }
      }
    } else if (this.linearAdOnStage && !this.adImaDai) {
      this.rmpAdsHelpers.setAdMute(true);
    }
    // update UI
    this.dom.volume.classList.remove('rmp-i-volume-off', 'rmp-i-volume-half', 'rmp-i-volume');
    this.dom.volume.classList.add('rmp-i-volume-off');
  }

  /** 
   * @private
   */
  #unmute() {
    if (!this.permanentMuted) {
      this.muted = false;
      try {
        this.dom.video.muted = false;
      } catch (warning) {
        console.warn(warning);
      }
      if (this.rmpGoogleCast && this.rmpGoogleCast.isCastMediaLoaded()) {
        if (this.googleCastCordova) {
          const volume = new window.chrome.cast.Volume(this.volumeBeforeMute, false);
          const volumeRequest = new window.chrome.cast.media.VolumeRequest(volume);
          this.castMedia.setVolume(volumeRequest);
        } else {
          if (this.remotePlayer.isMuted) {
            this.rmpGoogleCast.remotePlayerController.muteOrUnmute();
          }
        }
      } else if (this.linearAdOnStage && !this.adImaDai) {
        this.rmpAdsHelpers.setAdMute(false);
      }
      // update UI
      this.dom.volume.classList.remove('rmp-i-volume-off', 'rmp-i-volume-half', 'rmp-i-volume');
      const currentVolume = this.volume;
      this.rmpVolumeManagement.updateVolumeIcon(currentVolume);
    }
  }

  /** 
   * Set the current player mute state 
   * @type {(muted: boolean) => void} 
   */
  set mute(muted) {
    if (typeof muted !== 'boolean') {
      this.rmpUtils.warning(1003);
      return;
    }
    if (!this.playerReady) {
      this.rmpUtils.warning(1004);
      return;
    }
    if (muted) {
      this.#mute();
    } else {
      this.#unmute();
    }

  }

  /******************************************* PlaybackRate API *******************************************/
  /** 
   * Get the current player playback rate 
   * @type {() => number} 
   */
  get playbackRate() {
    if (this.dom.video) {
      return this.dom.video.playbackRate;
    }
    return -1;
  }

  /** 
   * Set the current player playback rate 
   * @type {(rate: number) => void} 
   */
  set playbackRate(rate) {
    if (!FW.isFiniteNumber(rate)) {
      this.rmpUtils.warning(1003);
      return;
    }
    if (!this.playerReady) {
      this.rmpUtils.warning(1004);
      return;
    }
    const paused = this.paused;
    if (!paused && Env.isIos[0]) {
      this.pause();
    }
    try {
      this.dom.video.playbackRate = rate;
    } catch (warning) {
      console.warn(warning);
    }
    if (!paused && Env.isIos[0]) {
      this.play().catch(warning => {
        this.rmpUtils.warning(1014, warning);
      });
    }

  }

  /******************************************* Captions API *******************************************/
  /** 
   * @type {(lng: string) => void} 
   */
  showCaptions(lng) {
    if (typeof lng !== 'string') {
      Logger.print('api', 'invalid showCaptions parameter');
      return;
    }
    if (this.rmpCCManagement) {
      this.rmpCCManagement.ccMenuItemInteraction(lng);
    }
  }

  /** 
   * @type {() => void} 
   */
  hideCaptions() {
    if (this.rmpCCManagement) {
      this.rmpCCManagement.ccMenuItemInteraction('off');
    }
  }

  /** 
   * @type {() => string} 
   */
  get ccVisibleLanguage() {
    if (this.dom.captionsOverlayLevelsArea) {
      const ccButtons = this.dom.captionsOverlayLevelsArea.querySelectorAll('.rmp-overlay-level');
      if (ccButtons.length > 0) {
        for (let i = 0, len = ccButtons.length; i < len; i++) {
          const button = ccButtons[i];
          if (button.classList.contains('rmp-overlay-level-active')) {
            return button.getAttribute('lang');
          }
        }
      }
    }
    return '';
  }

  /** 
   * @type {() => object[]} 
   */
  get captionsData() {
    if (this.rmpCCManagement) {
      return this.rmpCCManagement.getCCData();
    }
    return [{}];
  }

  /** 
   * get|set Array of TextTracks representing the current list of loaded text tracks.
For captionsList getter: tracks that fail to load will not be listed.
For captionsList getter: use when alltexttracksloaded API event fires.
   * @return {TextTrack[]}
   */
  get captionsList() {
    if (this.hasExternalCCFiles && this.rmpCCManagement && this.rmpCCManagement.ccFilesReady) {
      return this.loadedCCFiles;
    }
    return [{ lng: '', name: '', uri: '', data: null, default: false }];
  }

  /** 
   * get|set Array of TextTracks representing the current list of loaded text tracks.
For captionsList getter: tracks that fail to load will not be listed.
For captionsList getter: use when alltexttracksloaded API event fires.
   * @type {(ccFiles: TextTrack[]) => void} 
   */
  set captionsList(ccFiles) {
    if (!FW.isValidCCFiles(ccFiles)) {
      this.rmpUtils.warning(1003);
      return;
    }
    if (!this.playerReady) {
      this.rmpUtils.warning(1004);
      return;
    }
    if (this.rmpCCManagement) {
      this.rmpCCManagement.destroy();
    }
    if (Env.isIos[0]) {
      this.ccFiles = ccFiles;
      this.hasExternalCCFiles = true;
      this.rmpInitManagement.ccFilesNativeTextTracksLoadedCount = 0;
      const textTracks = this.dom.video.querySelectorAll('track');
      if (textTracks.length > 0) {
        for (let i = 0, len = textTracks.length; i < len; i++) {
          FW.removeElement(textTracks[i]);
        }
      }
      this.rmpInitManagement.iosAddTrackElements();
    } else {
      this.rmpVttJS = new VttJS(this, ccFiles);
      this.rmpVttJS.init();
    }

  }

  /******************************************* Poster API *******************************************/
  /** 
   * @type {() => void} 
   */
  showPoster() {
    if (this.dom.poster) {
      this.dom.poster.style.display = 'block';
    }
  }

  /** 
   * @type {() => void} 
   */
  hidePoster() {
    if (this.dom.poster) {
      this.dom.poster.style.display = 'none';
    }
  }

  /** 
   * @type {() => string} 
   */
  get poster() {
    return this.posterUri;
  }

  /** 
   * @type {(posters: string[]|string) => void} 
   */
  set poster(posters) {
    let posterUrl = '';
    if (Array.isArray(posters) && typeof posters[0] === 'string') {
      posterUrl = FW.getPoster(posters);
    } else if (typeof posters === 'string') {
      posterUrl = posters;
    } else {
      Logger.print('api', 'error with poster - expected String or non-empty Array');
      posterUrl = FW.blackFrame;
    }
    Logger.print('api', 'set poster at URI ' + posterUrl);
    this.posterUri = posterUrl;
    // removes poster hidden for in case no poster >> show first frame of live stream
    this.dom.poster.classList.remove('rmp-no-display');
    this.rmpPosterManagement.unwirePosterLoadEvents();
    this.rmpPosterManagement.init();
  }

  /******************************************* Sizing API *******************************************/
  /** 
   * @type {() => void} 
   */
  resize() {
    if (this.rmpSizeManagement) {
      this.rmpSizeManagement.resizePlayer();
    }
  }

  /** 
   * @typedef {object} PlayerSize
   * @property {number} [width]
   * @property {number} [height]
   * @return {PlayerSize}
   */
  getPlayerSize() {
    return {
      width: FW.getPrecisionWidthWithPaddingAndBorder(this.dom.container),
      height: FW.getPrecisionHeightWithPaddingAndBorder(this.dom.container)
    };
  }

  /**
   * @type {(x:string, y:string, fullscreenFullWindowMode:boolean) => void}
   */
  setPlayerSize(inputPlayerSize) {
    if (typeof inputPlayerSize.width !== 'number' || inputPlayerSize.width <= 0 ||
      typeof inputPlayerSize.height !== 'number' || inputPlayerSize.height <= 0) {
      this.rmpUtils.warning(1003);
      return;
    }
    if (!this.playerReady) {
      this.rmpUtils.warning(1004);
      return;
    }
    let validatedFullscreenFullWindowMode = false;
    if (typeof inputPlayerSize.fullscreenFullWindowMode === 'boolean') {
      validatedFullscreenFullWindowMode = inputPlayerSize.fullscreenFullWindowMode;
    }
    if (this.isInFullscreen && !this.fullscreenFullWindowMode) {
      this.rmpUtils.warning(1026);
      return;
    }
    if (this.iframeMode || (this.autoHeightMode && !this.fullscreenFullWindowMode)) {
      this.rmpUtils.warning(1027);
      return;
    }
    const width = inputPlayerSize.width;
    const height = inputPlayerSize.height;
    Logger.print('api', 'playerSize called with width: ' + width + ' and height: ' + height);
    if (validatedFullscreenFullWindowMode) {
      this.width = width;
      this.height = height;
    } else {
      this.rmpSizeManagement.initialWidth = width;
      this.width = width;
      this.rmpSizeManagement.initialHeight = height;
      this.height = height;
    }
    if (this.audioOnly) {
      this.height = 48;
    }
    this.resize();
  }

  /** 
   * @typedef {object} Ratio
   * @property {number} player
   * @property {number} media
   * @return {Ratio}
   */
  get ratio() {
    const currentRatio = {
      player: -1,
      media: -1
    };
    const playerSize = this.getPlayerSize();
    if (playerSize.width > 0 && playerSize.height > 0) {
      currentRatio.player = playerSize.width / playerSize.height;
    }
    if (this.dom.video) {
      const videoWidth = this.dom.video.videoWidth;
      const videoHeight = this.dom.video.videoHeight;
      if (videoWidth > 0 && videoHeight > 0) {
        currentRatio.media = videoWidth / videoHeight;
      }
    }
    return currentRatio;
  }

  /******************************************* Loading UI API *******************************************/
  /** 
   * @type {() => boolean} 
   */
  get waitingUI() {
    if (this.dom.container.classList.contains('rmp-waiting')) {
      return true;
    }
    return false;
  }

  /** 
   * @type {(showWaitingUI: boolean) => void} 
   */
  set waitingUI(showWaitingUI) {
    if (typeof showWaitingUI !== 'boolean') {
      this.rmpUtils.warning(1003);
      return;
    }
    if (this.rmpUIManagement) {
      if (showWaitingUI && !this.waitingUI) {
        this.rmpUIManagement.showLoadingSpin();
      } else if (!showWaitingUI && this.waitingUI) {
        this.rmpUIManagement.hideLoadingSpin();
      }
    }
  }

  /******************************************* Logo API *******************************************/
  /** 
   * @return {{imgUri: string, clickUri: string, position: string, margin: string, watermark: boolean, pauseContentOnLogoClick: boolean}}
   */
  get logoConfiguration() {
    return this.logo;
  }

  /** 
   * @type {(logoObject: {imgUri: string, clickUri: string, position: string, margin: string, watermark: boolean, pauseContentOnLogoClick: boolean}) => void} 
   */
  set logoConfiguration(logoObject) {
    if (!this.playerReady) {
      this.rmpUtils.warning(1004);
      return;
    }
    this.logo = logoObject;
    this.rmpLogo.filter();
    if (this.logo.imgUri === '') {
      this.rmpLogo.hide();
    } else {
      const hasInitLogo = this.dom.container.querySelector('.rmp-logo');
      if (hasInitLogo) {
        this.rmpLogo.update();
      } else {
        this.rmpLogo.init();
      }
      this.rmpLogo.show();
    }

  }

  /******************************************* Thumbnails API *******************************************/
  /** 
   * @type {() => string} 
   */
  get thumbnails() {
    return this.seekBarThumbnailsLoc;
  }

  /** 
   * @type {(uri: string) => void} 
   */
  set thumbnails(uri) {
    if (typeof uri !== 'string' || uri === '') {
      this.rmpUtils.warning(1003);
      return;
    }
    if (!this.playerReady) {
      this.rmpUtils.warning(1004);
      return;
    }
    this.seekBarThumbnailsLoc = uri;
    if (this.rmpThumbnails) {
      this.rmpThumbnails.destroy();
    }
    this.rmpThumbnails = new Thumbnails(this);
    this.rmpThumbnails.init();

  }

  /******************************************* 360 API *******************************************/
  /** 
   * @typedef {object} CameraView
   * @property {number} lat
   * @property {number} lon
   * @property {number} fov
   * @return {CameraView}
   */
  get cameraView() {
    let lat = -1;
    let lon = -1;
    let fov = -1;
    if (this.rmpThreeSixty) {
      lat = this.rmpThreeSixty.lat;
      lon = this.rmpThreeSixty.lon;
      fov = this.rmpThreeSixty.fov;
    }
    return {
      lat,
      lon,
      fov
    };
  }

  /** 
   * @type {(newCameraView: CameraView) => void} 
   */
  set cameraView(newCameraView) {
    const currentCameraView = this.cameraView;
    let lat = currentCameraView.lat;
    if (typeof newCameraView.lat === 'number') {
      lat = newCameraView.lat;
    }
    let lon = currentCameraView.lon;
    if (typeof newCameraView.lon === 'number') {
      lon = newCameraView.lon;
    }
    let fov = currentCameraView.fov;
    if (typeof newCameraView.fov === 'number') {
      fov = newCameraView.fov;
    }
    if (this.rmpThreeSixty) {
      this.rmpThreeSixty.setCamLatLongFov360(lat, lon, fov, true);
    }
  }

  /******************************************* Chapters API *******************************************/
  /** 
   * @typedef {object} Chapter
   * @property {number} start
   * @property {number} end
   * @property {string} title
   * @return {Chapter[]}
   */
  get chaptersData() {
    if (this.rmpChapters && this.rmpChapters.array.length > 0) {
      return this.rmpChapters.array;
    }
    return [{ start: -1, end: -1, title: '' }];
  }

  /** 
   * @return {string}
   */
  get chapters() {
    return this.chaptersLoc;
  }

  /** 
   * @type {(uri: string) => void} 
   */
  set chapters(uri) {
    if (typeof uri !== 'string' || uri === '') {
      this.rmpUtils.warning(1003);
      return;
    }
    if (!this.playerReady) {
      this.rmpUtils.warning(1004);
      return;
    }
    if (this.rmpChapters) {
      this.rmpChapters.destroy();
    }
    this.chaptersLoc = uri;
    this.rmpChapters = new Chapters(this);
    this.rmpChapters.init();
  }


  /** 
   * seek to chapter based on input. Returns Promise that resolves if seekToChapter successful (rejects otherwise)
   * @param {number} chapterIndex - the new chapter to seek to
   * @return {Promise<>}
   */
  seekToChapter(chapterIndex) {
    return new Promise((resolve, reject) => {
      if (!this.playerReady) {
        this.rmpUtils.warning(1004);
        reject(this.warningData);
        return;
      }
      const rmpChapters = this.rmpChapters;
      if (rmpChapters && rmpChapters.array.length > 0 && typeof chapterIndex === 'number' &&
        chapterIndex > -1 && typeof rmpChapters.array[chapterIndex] !== 'undefined') {
        this.seekTo(rmpChapters.array[chapterIndex].start).then(() => {
          resolve();
        }).catch(warning => {
          this.rmpUtils.warning(1016, warning);
          reject(this.warningData);
        });
      } else {
        this.rmpUtils.warning(1003);
        reject(this.warningData);
      }
    });
  }

  /******************************************* Google Cast API *******************************************/
  /** 
   * @type {() => boolean} 
   */
  get castMediaLoaded() {
    if (this.googleCast) {
      if (this.googleCastCordova) {
        return this.googleCastConnected;
      } else if (this.remotePlayer) {
        return this.remotePlayer.isMediaLoaded;
      }
    }
    return false;
  }

  /** 
   * @type {() => boolean} 
   */
  get castConnected() {
    return this.googleCastConnected;
  }

  /** 
   * @type {() => string} 
   */
  get castUrl() {
    return this.googleCastUrl;
  }

  /******************************************* metadata API *******************************************/
  /** 
   * @typedef {object} Metadata
   * @property {object} data
   * @property {string} type
   * @return {Metadata}
   */
  get metadata() {
    if (!FW.isEmptyObject(this.meta)) {
      return this.meta;
    }
    return {
      data: {},
      type: ''
    };
  }

  /******************************************* hls.js API *******************************************/
  /** 
   * @typedef {object} ManifestData
   * @property {object[]} levels
   * @property {object[]} audioTracks
   * @property {object[]} subtitles
   * @property {object[]} captions
   * @return {ManifestData}
   */
  get hlsManifestData() {
    if (this.rmpHlsJS) {
      return this.rmpHlsJS.hlsManifestData;
    }
    return {
      levels: [{}],
      audioTracks: [{}],
      subtitles: [{}],
      captions: [{}]
    };
  }

  /** 
   * @type {() => object} 
   */
  get hlsSessionData() {
    if (this.rmpHlsJS) {
      return this.rmpHlsJS.hlsSessionData;
    }
    return [];
  }

  /** 
   * @type {() => object} 
   */
  get hlsLevelData() {
    if (this.rmpHlsJS) {
      return this.rmpHlsJS.hlsLevelData;
    }
    return {};
  }

  /** 
   * @type {() => object} 
   */
  get hlsFragmentData() {
    if (this.rmpHlsJS) {
      return this.rmpHlsJS.hlsFragmentData;
    }
    return {};
  }

  /** 
   * @type {() => object} 
   */
  get hlsFragmentBeingPlayedData() {
    if (this.rmpHlsJS) {
      return this.rmpHlsJS.hlsFragmentBeingPlayedData;
    }
    return {};
  }

  /** 
   * @type {() => object} 
   */
  get hlsRawID3KLVSamplesData() {
    if (this.rmpHlsJS) {
      return this.rmpHlsJS.hlsRawID3KLVSamplesData;
    }
    return [];
  }

  /** 
   * @type {() => object} 
   */
  get hlsJSInstance() {
    if (!this.hlsJS) {
      return {};
    }
    return this.hlsJS;
  }

  /** 
   * @type {() => void} 
   */
  hlsStartLoad() {
    if (this.hlsJS) {
      Logger.print('api', 'hlsStartLoad called');
      this.hlsJS.startLoad();
    }
  }

  /** 
   * @type {() => void} 
   */
  hlsStopLoad() {
    if (this.hlsJS) {
      Logger.print('api', 'hlsStopLoad called');
      this.hlsJS.stopLoad();
    }
  }

  /******************************************* Error API *******************************************/
  /** 
   * @typedef {object} ErrorData
   * @property {number} code
   * @property {string} message
   * @property {object} event
   * @property {boolean} fatal
   * @return {ErrorData}
   */
  get errorData() {
    if (!this.innerErrorData) {
      return {
        code: -1,
        message: '',
        event: null,
        fatal: false
      };
    }
    return this.innerErrorData;
  }

  /** 
   * @typedef {object} WarningData
   * @property {number} code
   * @property {string} message
   * @property {object} event
   * @property {boolean} fatal
   * @return {WarningData}
   */
  get warningData() {
    if (!this.innerWarningData) {
      return {
        code: -1,
        message: '',
        event: null,
        fatal: false
      };
    }
    return this.innerWarningData;
  }

  /******************************************* Module API *******************************************/
  /** 
   * @type {(type: string) => boolean} 
   */
  getModuleOverlayVisible(type) {
    if (typeof type === 'string' && this.rmpModuleManagement &&
      this.rmpModuleManagement.openableTypes.indexOf(type) > -1 && this.dom[type + 'Overlay']) {
      return FW.isVisible(this.dom[type + 'Overlay']);
    }
    return false;
  }

  /** 
   * @type {(type: string, visible: boolean) => void} 
   */
  setModuleOverlayVisible(type, visible) {
    if (this.rmpModuleManagement && typeof visible === 'boolean' && typeof type === 'string' &&
      this.rmpModuleManagement.openableTypes.indexOf(type) > -1 && this.dom[type + 'Overlay']) {
      if (visible && !FW.isVisible(this.dom[type + 'Overlay'])) {
        this.rmpModuleManagement.open(type, false, null);
      } else if (!visible && FW.isVisible(this.dom[type + 'Overlay'])) {
        this.rmpModuleManagement.close(type);
      }
    }
  }

  /******************************************* Autoplay API *******************************************/
  /** 
   * @type {() => void} 
   */
  resetAutoplayCount() {
    if (LocalStorage.available(this.allowLocalStorage) && localStorage.rmpAutoplayCount) {
      delete localStorage.rmpAutoplayCount;
    }
  }

  /**
   * @private
   */
  #onAutoplayResultKnown(resolve) {
    this.off('autoplayfailure', this.onAutoplayResultKnownFn);
    this.off('autoplaysuccess', this.onAutoplayResultKnownFn);
    if (!this.autoplay) {
      resolve('no-autoplay');
    } else {
      if (this.autoplayMode === 'autoplay-with-sound') {
        resolve('autoplay-with-sound');
      } else {
        resolve('muted-autoplay');
      }
    }
  }

  /** 
   * Returns Promise that resolves to autoplay result at the moment player is trying to play content for first time. no-autoplay means autoplay attempt was a failure. Note that this method never rejects. 
   * autoplay-with-sound | muted-autoplay | no-autoplay
   * @type {() => string} 
   */
  getAutoplayResult() {
    return new Promise(resolve => {
      if (this.autoplayResultKnown || this.requestAutoplayCapabilitiesData) {
        resolve(this.autoplayMode);
      } else {
        this.onAutoplayResultKnownFn = this.#onAutoplayResultKnown.bind(this, resolve);
        this.one('autoplayfailure', this.onAutoplayResultKnownFn);
        this.one('autoplaysuccess', this.onAutoplayResultKnownFn);
      }
    });
  }

  /******************************************* Viewable API *******************************************/
  /** 
   * @type {() => boolean} 
   */
  get playerVisible() {
    if (this.rmpViewableObserver) {
      return this.rmpViewableObserver.playerVisible;
    }
    return false;
  }

  /******************************************* Frame API *******************************************/
  /** 
   * @type {() => number} 
   */
  get currentFramerate() {
    return this.frameRate;
  }

  /** 
   * @type {() => number} 
   */
  get droppedFramesCount() {
    // https://w3c.github.io/media-playback-quality/
    if (this.dom.video && this.dom.video.getVideoPlaybackQuality) {
      return this.dom.video.getVideoPlaybackQuality().droppedVideoFrames;
    }
    return -1;
  }

  /** 
   * @type {() => number} 
   */
  get currentFrameNumber() {
    const nowCurrentTime = this.currentTime;
    if (this.isVod && this.frameRate > -1 && nowCurrentTime >= 0) {
      const frameNumber = Math.round(((nowCurrentTime * this.frameRate) + 1000) / 1000);
      Logger.print('info', 'Current frame number is ' + frameNumber);
      if (nowCurrentTime === 0) {
        return 1;
      } else {
        return frameNumber;
      }
    }
    return -1;
  }

  /** 
   * @type {() => number} 
   */
  get totalFramesNumber() {
    const nowDuration = this.duration;
    if (this.isVod && this.frameRate > -1 && nowDuration > -1) {
      Logger.print('info', 'Raw total frames number is ' + (nowDuration * this.frameRate / 1000));
      Logger.print('info', 'Final total frames number is ' + Math.round(nowDuration * this.frameRate / 1000));
      return Math.round(nowDuration * this.frameRate / 1000);
    }
    return -1;
  }


  /** 
   * seek to a specific frame based on input. Returns Promise that resolves if seekToFrame successful (rejects otherwise).
   * @param {string} frameLocation - the frame to seek to (example: '10', 'next', 'previous')
   * @return {Promise<>}
   */
  seekToFrame(frameLocation) {
    return new Promise((resolve, reject) => {
      if (!this.playerReady) {
        this.rmpUtils.warning(1004);
        reject(this.warningData);
        return;
      }
      if (!this.isVod) {
        this.rmpUtils.warning(1040);
        reject(this.warningData);
        return;
      }
      if (typeof frameLocation === 'string' &&
        (frameLocation === 'next' || frameLocation === 'previous' || FW.isFiniteNumber(parseInt(frameLocation))) &&
        this.frameRate > -1) {
        const nowDuration = this.duration;
        if (nowDuration > -1) {
          let newCurrentTime;
          if (frameLocation === 'next') {
            newCurrentTime = this.currentFrameNumber / this.frameRate * 1000;
          } else if (frameLocation === 'previous') {
            newCurrentTime = (this.currentFrameNumber - 2) / this.frameRate * 1000;
          } else {
            const frameNumber = parseInt(frameLocation);
            if (frameNumber > 1) {
              newCurrentTime = ((frameNumber - 1) / this.frameRate) * 1000;
            } else if (frameNumber === 1) {
              newCurrentTime = 0;
            }
          }
          Logger.print('info', 'seekToFrame with seekTo value of ' + newCurrentTime + ' ms');
          this.seekTo(newCurrentTime).then(() => {
            resolve();
          }).catch(warning => {
            this.rmpUtils.warning(1016, warning);
            reject(this.warningData);
          });
        } else {
          this.rmpUtils.warning(1041);
          reject(this.warningData);
        }
      } else {
        this.rmpUtils.warning(1003);
        reject(this.warningData);
      }
    });
  }

  /******************************************* Sharing API *******************************************/
  /** 
   * @typedef {object} SharingData
   * @property {string} url
   * @property {string} code
   * @return {SharingData}
   */
  get sharingData() {
    return {
      url: this.sharingUrl,
      code: this.sharingCode
    };
  }

  /**
   * @private
   */
  #setSharingUrl(url) {
    if (typeof url === 'string' && url && this.rmpSharing) {
      const filteredSharingNetworks = this.rmpSharing.filteredSharingNetworks;
      this.sharingUrl = url;
      const container = this.dom.container;
      if (filteredSharingNetworks.indexOf('facebook') > -1) {
        const facebookIcon = container.querySelector('.rmp-i-facebook');
        if (facebookIcon) {
          facebookIcon.setAttribute('href', 'https://www.facebook.com/sharer/sharer.php?u=' +
            encodeURIComponent(url));
        }
      }
      if (filteredSharingNetworks.indexOf('x') > -1 || filteredSharingNetworks.indexOf('twitter') > -1) {
        const twitterIcon = container.querySelector('.rmp-i-x');
        if (twitterIcon) {
          twitterIcon.setAttribute('href', 'https://x.com/share?url=' +
            encodeURIComponent(url));
        }
      }
      if (filteredSharingNetworks.indexOf('linkedin') > -1) {
        const linkedinIcon = container.querySelector('.rmp-i-linkedin');
        if (linkedinIcon) {
          linkedinIcon.setAttribute('href', 'https://www.linkedin.com/shareArticle?mini=true&url=' +
            encodeURIComponent(url));
        }
      }
      if (filteredSharingNetworks.indexOf('reddit') > -1) {
        const redditIcon = container.querySelector('.rmp-i-reddit');
        if (redditIcon) {
          redditIcon.setAttribute('href', 'https://www.reddit.com/submit?url=' +
            encodeURIComponent(url));
        }
      }
      if (filteredSharingNetworks.indexOf('whatsapp') > -1) {
        const whatsappIcon = container.querySelector('.rmp-i-whatsapp');
        if (whatsappIcon) {
          whatsappIcon.setAttribute('href', 'whatsapp://send?text=' +
            encodeURIComponent(url));
        }
      }
      const sharingLinkInput = container.querySelector('#rmp-sharing-link-' + this.id);
      if (sharingLinkInput) {
        sharingLinkInput.value = url;
      }
    }
  }

  /**
   * @private
   */
  #setSharingCode(sharingCode) {
    const sharingCodeInput = this.dom.container.querySelector('#rmp-sharing-code-' + this.id);
    if (typeof sharingCode === 'string' && sharingCode && sharingCodeInput) {
      this.sharingCode = sharingCode;
      sharingCodeInput.value = this.sharingCode;
    }
  }


  /** 
   * @type {(data: SharingData) => void} 
   */
  set sharingData(data) {
    if (data && typeof data === 'object' && (data.url || data.code)) {
      if (data.url) {
        this.#setSharingUrl(data.url);
      }
      if (data.code) {
        this.#setSharingCode(data.code);
      }
    }
  }

  /******************************************* CuePoint API *******************************************/
  /** 
   * @type {() => CuePoint} 
   */
  get cuePointData() {
    if (this.rmpCuePoints) {
      return {
        time: this.rmpCuePoints.currentTime,
        text: this.rmpCuePoints.currentText
      };
    }
    return {
      time: -1,
      text: ''
    };
  }

  /********************************* Video ads API - Mix IMA | rmp-vast *********************************/
  /** 
   * @type {() => string} 
   */
  get adParserConfiguration() {
    return this.adParser;
  }

  /** 
   * @type {() => boolean} 
   */
  get vpaidCreative() {
    if (this.adOnStage) {
      return this.isVPAID;
    }
    return false;
  }

  /** 
   * @type {() => boolean} 
   */
  get adLinear() {
    if (this.adOnStage) {
      if (this.imaAdsManager || this.rmpAdsImaDai) {
        return this.imaAdLinear;
      } else if (this.rmpVast) {
        return this.rmpVast.adLinear;
      } else if (this.shakaAd && this.shakaAd.isLinear()) {
        return this.shakaAd.isLinear();
      }
    }
    return false;
  }

  /** 
   * @type {() => boolean} 
   */
  get adOnStage() {
    if (this.ads) {
      if (this.googleCastConnected) {
        if (this.remotePlayer && this.remotePlayer.isPlayingBreak) {
          return true;
        }
        return false;
      } else if (this.imaAdsManager || this.rmpAdsImaDai) {
        return this.imaAdOnStage;
      } else if (this.rmpVast) {
        return this.rmpVast.adOnStage;
      }
    } else if (this.rmpShakaAWSMediaTailor) {
      return this.rmpShakaAWSMediaTailor.adOnStage;
    }
    return false;
  }

  /** 
   * @type {() => boolean} 
   */
  get linearAdOnStage() {
    return this.adOnStage && this.adLinear;
  }

  /** 
   * @type {() => string} 
   */
  get adSystem() {
    if (this.adOnStage) {
      if (this.imaAdsManager || this.rmpAdsImaDai) {
        return this.imaAdSystem;
      } else if (this.rmpVast) {
        const adSystem = this.rmpVast.adSystem;
        if (adSystem.value) {
          return adSystem.value;
        }
      }
    }
    return '';
  }

  /** 
   * @type {() => string} 
   */
  get adContentType() {
    if (this.adOnStage) {
      if (this.imaAdsManager) {
        return this.imaAdContentType;
      } else if (this.rmpVast) {
        return this.rmpVast.adContentType;
      }
    }
    return '';
  }

  /** 
   * @type {() => string} 
   */
  get adTitle() {
    if (this.adOnStage) {
      if (this.imaAdsManager || this.rmpAdsImaDai) {
        return this.imaAdTitle;
      } else if (this.rmpVast) {
        return this.rmpVast.adTitle;
      }
    }
    return '';
  }

  /** 
   * @type {() => string} 
   */
  get adDescription() {
    if (this.adOnStage) {
      if (this.imaAdsManager || this.rmpAdsImaDai) {
        return this.imaAdDescription;
      } else if (this.rmpVast) {
        return this.rmpVast.adDescription;
      }
    }
    return '';
  }

  /** 
   * @type {() => string} 
   */
  get adMediaUrl() {
    if (this.adOnStage) {
      if (this.imaAdsManager) {
        return this.imaAdMediaUrl;
      } else if (this.rmpVast) {
        return this.rmpVast.adMediaUrl;
      }
    }
    return '';
  }

  /** 
   * @type {() => number} 
   */
  get adMediaHeight() {
    if (this.adOnStage) {
      if (this.imaAdsManager || this.rmpAdsImaDai) {
        let currentAdMediaHeight;
        if (!this.imaAdLinear) {
          currentAdMediaHeight = this.imaAdNonLinearHeight;
        } else {
          currentAdMediaHeight = this.imaAdLinearHeight;
        }
        if (typeof currentAdMediaHeight === 'number') {
          return currentAdMediaHeight;
        }
      } else if (this.rmpVast) {
        return this.rmpVast.adMediaHeight;
      }
    }
    return -1;
  }

  /** 
   * @type {() => number} 
   */
  get adMediaWidth() {
    if (this.adOnStage) {
      if (this.imaAdsManager || this.rmpAdsImaDai) {
        let currentAdMediaWidth;
        if (!this.imaAdLinear) {
          currentAdMediaWidth = this.imaAdNonLinearWidth;
        } else {
          currentAdMediaWidth = this.imaAdLinearWidth;
        }
        if (typeof currentAdMediaWidth === 'number') {
          return currentAdMediaWidth;
        }
      } else if (this.rmpVast) {
        return this.rmpVast.adMediaWidth;
      }
    }
    return -1;
  }

  /** 
   * @type {() => number} 
   */
  get adCurrentTime() {
    if (this.rmpAdsImaDai) {
      return this.rmpAdsImaDai.adCurrentTime * 1000;
    } else if (this.linearAdOnStage) {
      if (this.imaAdsManager) {
        const nowAdDuration = this.adDuration;
        const timeRemaining = this.imaAdsManager.getRemainingTime();
        if (FW.isFiniteNumber(timeRemaining) && timeRemaining >= 0 && nowAdDuration >= 0) {
          return (nowAdDuration - (timeRemaining * 1000));
        }
      } else if (this.rmpVast) {
        return this.rmpVast.adCurrentTime;
      } else if (this.shakaAdManager && this.shakaAd) {
        return this.adDuration - (this.shakaAd.getRemainingTime() * 1000);
      }
    }
    return -1;
  }

  /** 
   * @type {() => number} 
   */
  get adDuration() {
    if (this.linearAdOnStage) {
      if (this.imaAdsManager || this.rmpAdsImaDai) {
        if (FW.isFiniteNumber(this.imaAdDuration) && this.imaAdDuration >= 0) {
          return this.imaAdDuration * 1000;
        }
      } else if (this.rmpVast) {
        return this.rmpVast.adDuration;
      } else if (this.shakaAdManager && this.shakaAd) {
        return this.shakaAd.getDuration() * 1000;
      }
    }
    return -1;
  }

  /** 
   * @type {() => boolean} 
   */
  get adPaused() {
    if (this.adImaDai) {
      return this.paused;
    } else if (this.linearAdOnStage) {
      if (this.imaAdsManager) {
        return this.imaAdPaused;
      } else if (this.rmpVast) {
        return this.rmpVast.adPaused;
      }
    }
    return false;
  }

  /** 
   * @type {() => string} 
   */
  get currentAdTagUrl() {
    if (this.adOnStage) {
      return this.adTagUrl;
    }
    return '';
  }

  /** 
   * @private
   */
  #resetOutstreamAdUI() {
    FW.addEvents(['touchend', 'click'], this.dom.playPause, this.initiatePlayback);
    if (this.isMobile) {
      FW.addEvents(['touchend', 'click'], this.dom.centralMobileUI, this.initiatePlayback);
    } else {
      FW.addEvents(['touchend', 'click'], this.dom.overlayButton, this.initiatePlayback);
    }
    this.rmpUIManagement.showCentralPlay();
  }

  /** 
   * @private
   */
  #onAdDestroyLoadAds() {
    if (this.rmpAdsIma) {
      this.off('adcontentresumerequested', this.onAdDestroyLoadAdsFn);
      this.rmpAdsIma.resetAdsForNewRequest();
      this.rmpAdsIma.requestImaAds(this.adTagUrl);
    } else if (this.rmpAdsRmpVast) {
      this.off('addestroyed', this.onAdDestroyLoadAdsFn);
      this.rmpAdsRmpVast.restoreContentUI();
      this.rmpAdsRmpVast.requestRmpVastAds(this.adTagUrl);
    }
    if (this.adOutStream) {
      this.#resetOutstreamAdUI();
    }
  }

  /** 
   * load a VAST tag at input parameter adTag - pass optional adTagWaterfall parameter for client-side waterfalling. Returns Promise that resolves if loadAds successful (rejects otherwise)
   * @param {string} adTag - URI to VAST tag
   * @param {string[]} [adTagWaterfall] - optional Array of VAST tag URI to be used for client-side waterfalling
   * @param {boolean} [firstPlayAdRequest]
   * @return {Promise<>}
   */
  loadAds(adTag, adTagWaterfall, firstPlayAdRequest) {
    return new Promise((resolve, reject) => {
      if (typeof adTag !== 'string' || adTag === '') {
        this.rmpUtils.warning(1003);
        reject(this.warningData);
        return;
      }
      if (!this.playerReady) {
        this.rmpUtils.warning(1004);
        reject(this.warningData);
        return;
      }
      if (!this.ads) {
        this.rmpUtils.warning(10005);
        reject(this.warningData);
        return;
      }
      if (this.adImaDai) {
        this.rmpUtils.warning(10012);
        reject(this.warningData);
        return;
      }
      if (this.googleCastConnected) {
        this.rmpUtils.warning(10013);
        reject(this.warningData);
        return;
      }
      this.adTagUrl = this.rmpAdsHelpers.replaceAdTagVar(adTag);
      this.rmpInitManagement.originalAdTagUrl = this.adTagUrl;
      let isInternalLoadAdsRequest = false;
      let hasAdTagWaterfall = false;
      if (Array.isArray(adTagWaterfall) && adTagWaterfall[0]) {
        if (adTagWaterfall[0] === 'internal') {
          isInternalLoadAdsRequest = true;
        } else {
          this.adTagWaterfall = adTagWaterfall;
          this.rmpInitManagement.originalAdTagWaterfall = this.adTagWaterfall;
          hasAdTagWaterfall = true;
        }
      }
      if (firstPlayAdRequest) {
        if (this.rmpAdsIma) {
          this.rmpAdsIma.requestImaAds(this.adTagUrl);
        } else if (this.rmpAdsRmpVast) {
          this.rmpAdsRmpVast.requestRmpVastAds(this.adTagUrl);
        }
        resolve();
        return;
      }
      Logger.print('api', 'loadAds API called with URI: ' + this.adTagUrl);
      this.onAdDestroyLoadAdsFn = this.#onAdDestroyLoadAds.bind(this);
      let _onAdLoaded, _onAdError;
      _onAdLoaded = () => {
        this.off('aderror', _onAdError);
        resolve();
      };
      _onAdError = () => {
        this.off('adloaded', _onAdLoaded);
        if (hasAdTagWaterfall || isInternalLoadAdsRequest) {
          resolve();
        } else {
          this.rmpUtils.warning(10016);
          reject(this.warningData);
        }
      };
      if (this.rmpAdsIma) {
        if (!this.rmpPlaybackManagement.adImaDisplayContainerInitialized) {
          this.rmpUtils.warning(10014);
          reject(this.warningData);
          return;
        }
        this.one('adloaded', _onAdLoaded);
        this.one('aderror', _onAdError);
        if (this.linearAdOnStage) {
          this.on('adcontentresumerequested', this.onAdDestroyLoadAdsFn);
          this.stopAds().catch(warning => {
            this.rmpUtils.warning(1021, warning);
            reject(this.warningData);
          });
        } else {
          this.onAdDestroyLoadAdsFn();
        }
      } else if (this.rmpAdsRmpVast) {
        if (!this.rmpVast.initialized) {
          this.rmpUtils.warning(10015);
          reject(this.warningData);
          return;
        }
        this.one('adloaded', _onAdLoaded);
        this.one('aderror', _onAdError);
        if (this.linearAdOnStage) {
          this.on('addestroyed', this.onAdDestroyLoadAdsFn);
          this.stopAds().catch(error => {
            this.rmpUtils.warning(1021, error);
            reject(this.warningData);
          });
        } else {
          this.onAdDestroyLoadAdsFn();
        }
      }

    });
  }

  /** 
   * stop playing current advertisement and free up player resources. Returns Promise that resolves if stopAds successful (rejects otherwise - will also reject if no ad on stage)
   * @return {Promise<>}
   */
  stopAds() {
    Logger.print('api', 'stopAds requested');
    return new Promise((resolve, reject) => {
      if (!this.playerReady) {
        this.rmpUtils.warning(1004);
        reject(this.warningData);
        return;
      }
      if (!this.adOnStage) {
        this.rmpUtils.warning(10017);
        reject(this.warningData);
        return;
      }
      if (this.imaAdsManager) {
        if (!this.adLinear) {
          this.imaAdOnStage = false;
          this.imaAdsManager.stop();
          resolve();
        } else {
          this.one('adcontentresumerequested', () => {
            resolve();
          });
          this.imaAdsManager.stop();
        }
      } else if (this.rmpVast) {
        this.one('addestroyed', () => {
          resolve();
        });
        this.rmpVast.stopAds();
      }
    });
  }

  /** 
   * skip current advertisement and free up player resources - if the advertisement can be skipped. Returns Promise that resolves if skipAd successful (rejects otherwise - will also reject if ad is not skippable)
   * @return {Promise<>}
   */
  skipAd() {
    return new Promise((resolve, reject) => {
      if (!this.playerReady) {
        this.rmpUtils.warning(1004);
        reject(this.warningData);
        return;
      }
      if (!this.adOnStage) {
        this.rmpUtils.warning(10018);
        reject(this.warningData);
        return;
      }
      if (this.adSkippableState) {
        if (this.imaAdsManager) {
          this.one('adcontentresumerequested', () => {
            resolve();
          });
          this.imaAdsManager.skip();
        } else if (this.rmpVast) {
          if (this.rmpVast.isSkippableAd || this.vpaidCreative) {
            this.one('addestroyed', () => {
              resolve();
            });
            this.rmpVast.skipAd();
          }
        }
      } else {
        this.rmpUtils.warning(10019);
        reject(this.warningData);
      }
    });
  }

  /** 
   * @typedef {object} AdSchedule
   * @property {string} [preroll]
   * @property {string[][]} [midroll]
   * @property {string} [postroll]
   * @return {AdSchedule}
   */
  get adScheduleConfiguration() {
    if (this.rmpInitManagement) {
      return this.rmpInitManagement.originalAdSchedule;
    }
    return {};
  }

  /** 
   * @type {(newAdSchedule: AdSchedule) => void} 
   */
  set adScheduleConfiguration(newAdSchedule) {
    if (!FW.isEmptyObject(newAdSchedule) &&
      (newAdSchedule.preroll || newAdSchedule.midroll || newAdSchedule.postroll) &&
      this.rmpAdScheduler) {
      Logger.print('api', 'updateAdSchedule with new schedule following:', newAdSchedule);
      this.rmpAdScheduler.update(newAdSchedule);
    } else {
      Logger.print('api', 'error with updateAdSchedule - invalid input');
    }
  }

  /** 
   * @type {() => boolean} 
   */
  get adSkippableState() {
    if (this.rmpAdsImaDai) {
      return this.rmpAdsImaDai.isSkippable;
    } else if (this.adOnStage) {
      if (this.imaAdsManager) {
        return this.imaAdsManager.getAdSkippableState();
      } else if (this.rmpVast) {
        if (this.rmpVast.isSkippableAd || this.vpaidCreative) {
          return this.rmpVast.adSkippableState;
        }
      }
    }
    return false;
  }

  /** 
   * @type {() => number} 
   */
  get adSkipTimeOffset() {
    if (this.adOnStage) {
      if (this.imaAdsManager || this.rmpAdsImaDai) {
        return this.imaAdSkipTimeOffset;
      } else if (this.rmpVast) {
        return this.rmpVast.skipTimeOffset;
      }
    }
    return -1;
  }

  /** 
   * @type {() => boolean} 
   */
  get adUI() {
    return this.hasAdUI;
  }

  /** 
   * @type {(inputAdUI: boolean) => void} 
   */
  set adUI(inputAdUI) {
    if (typeof inputAdUI !== 'boolean') {
      Logger.print('api', `error with adUI setter - expected boolean parameter instead saw ${typeof adUI}`);
      return;
    }
    if (this.rmpAdsHelpers) {
      Logger.print('api', `adUI setter called with input ${inputAdUI}`);
      this.rmpAdsHelpers.setUIForAds(inputAdUI);
    }
  }

  /** 
   * getAdPosition, getIsBumper, getMaxDuration, getPodIndex, getTimeOffset, getTotalAds > for Google IMA
   * adPodCurrentIndex, adPodLength > for rmp-vast
   * @typedef {object} AdPodInfo
   * @property {function} [getAdPosition]
   * @property {function} [getIsBumper]
   * @property {function} [getMaxDuration]
   * @property {function} [getPodIndex]
   * @property {function} [getTimeOffset]
   * @property {function} [getTotalAds]
   * @property {number} [adPodCurrentIndex]
   * @property {number} [adPodLength]
   * @return {AdPodInfo}
   */
  get adPodInfo() {
    if (this.adOnStage) {
      if (this.imaAdsManager) {
        return this.imaAdPodInfo || {};
      } else if (this.rmpVast) {
        return this.rmpVast.adPodInfo;
      }
    }
    return {};
  }

  /** 
   * @type {() => object[]} 
   */
  get adUniversalAdIds() {
    if (this.adOnStage) {
      if (this.imaAdsManager || this.rmpAdsImaDai) {
        if (this.imaAdUniversalAdIds.length > 0) {
          const arrayAdUniversalAdIds = [];
          this.imaAdUniversalAdIds.forEach(value => {
            arrayAdUniversalAdIds.push({
              idRegistry: value.getAdIdRegistry(),
              value: value.getAdIdValue()
            });
          });
          return arrayAdUniversalAdIds;
        }
      } else if (this.rmpVast) {
        return this.rmpVast.adUniversalAdIds;
      }
    }
    return [{}];
  }

  /** 
   * @type {() => string} 
   */
  get advertiserName() {
    if (this.adOnStage) {
      if (this.imaAdsManager || this.rmpAdsImaDai) {
        return this.imaAdvertiserName;
      } else if (this.rmpVast) {
        const adAdvertiser = this.rmpVast.adAdvertiser;
        if (adAdvertiser.value) {
          return adAdvertiser.value;
        }
      }
    }
    return '';
  }

  /** 
   * @type {() => string} 
   */
  get adSurveyUrl() {
    if (this.adOnStage) {
      if (this.imaAdsManager) {
        return this.imaAdSurveyUrl;
      } else if (this.rmpVast) {
        const adSurvey = this.rmpVast.adSurvey;
        if (adSurvey && adSurvey.value) {
          return adSurvey.value;
        }
      }
    }
    return '';
  }

  /** 
   * @param {number} adSlotWidth
   * @param {number} adSlotHeight
   * @param {object} [opt_settings]
   * @return {object[]}
   */
  getCompanionAds(adSlotWidth, adSlotHeight, opt_settings) {
    if (!this.ads) {
      this.rmpUtils.warning(10005);
      return [{}];
    }
    if (!this.playerReady) {
      this.rmpUtils.warning(1004);
      return [{}];
    }
    let companionWidth = 300;
    if (typeof adSlotWidth === 'number' && adSlotWidth > 0) {
      companionWidth = adSlotWidth;
    }
    let companionHeight = 250;
    if (typeof adSlotHeight === 'number' && adSlotHeight > 0) {
      companionHeight = adSlotHeight;
    }
    if (this.adParser === 'ima') {
      if (this.adImaDai) {
        if (typeof this.currentAdForCompanion.getCompanionAds === 'function') {
          return this.currentAdForCompanion.getCompanionAds();
        }
      } else if (this.imaAdsManager) {
        const companionAds = this.currentAdForCompanion.getCompanionAds(companionWidth, companionHeight, opt_settings);
        if (Array.isArray(companionAds) && companionAds.length > 0) {
          return companionAds;
        }
      }
    } else if (this.rmpVast) {
      const companionAds = this.rmpVast.getCompanionAdsList(companionWidth, companionHeight);
      if (Array.isArray(companionAds) && companionAds.length > 0) {
        return companionAds;
      }
    }
    return [{}];
  }

  /** 
   * Detect ad-blocker. Returns a Promise that resolves to a Boolean indicating if an ad blocker has been detected on page (true === adblocker detected) or rejects if ads setting is false and this is not a AWS Media Tailor stream
   * @return {Promise<boolean>}
   */
  getAdBlock() {
    return new Promise((resolve, reject) => {
      if (this.adParserLoadError) {
        resolve(true);
      } else if (this.rmpAdsHelpers) {
        AdBlocker.hasAdblock().then(adBlockerDetected => {
          if (adBlockerDetected) {
            resolve(true);
          } else {
            resolve(false);
          }
        });
      } else {
        this.rmpUtils.warning(10003);
        reject(this.warningData);
      }
    });
  }

  /********************************* Video Advertisement Error API *********************************/
  /** 
   * @type {() => string} 
   */
  get adErrorMessage() {
    if (this.rmpAdsImaDai) {
      return this.rmpAdsImaDai.errorMessage;
    } else if (this.rmpAdsIma) {
      return this.rmpAdsIma.adErrorMessage;
    } else if (this.rmpVast) {
      return this.rmpVast.adErrorMessage;
    }
    return '';
  }

  /** 
   * @type {() => number} 
   */
  get adVastErrorCode() {
    if (this.rmpAdsIma) {
      return this.rmpAdsIma.adVastErrorCode;
    } else if (this.rmpVast) {
      return this.rmpVast.adVastErrorCode;
    }
    return -1;
  }

  /** 
   * @type {() => string} 
   */
  get adErrorType() {
    if (this.rmpAdsIma) {
      return this.rmpAdsIma.adErrorType;
    } else if (this.rmpVast) {
      return this.rmpVast.adErrorType;
    }
    return '';
  }

  /** 
   * @type {() => number} 
   */
  get adErrorCode() {
    if (this.rmpAdsIma) {
      return this.rmpAdsIma.adErrorCode;
    }
    return -1;
  }

  /********************************* Video ads API - IMA specific *********************************/
  /** 
   * @type {() => object} 
   */
  get adsManager() {
    if (this.imaAdsManager) {
      return this.imaAdsManager;
    }
    return {};
  }

  /** 
   * @type {() => string} 
   */
  get adID() {
    if (this.adOnStage) {
      return this.imaAdID;
    }
    return '';
  }

  /** 
   * @type {() => string} 
   */
  get adCreativeAdId() {
    if (this.adOnStage) {
      return this.imaAdCreativeAdId;
    }
    return '';
  }

  /** 
   * @type {() => string} 
   */
  get adCreativeId() {
    if (this.adOnStage) {
      return this.imaAdCreativeId;
    }
    return '';
  }

  /** 
   * @type {() => string} 
   */
  get adDealId() {
    if (this.adOnStage) {
      return this.imaAdDealId;
    }
    return '';
  }

  /** 
   * @type {() => string} 
   */
  get adApiFramework() {
    if (this.adOnStage) {
      if (typeof this.imaAdApiFramework === 'string') {
        return this.imaAdApiFramework;
      }
    }
    return '';
  }

  /** 
   * @type {() => object} 
   */
  get adTraffickingParameters() {
    if (this.adOnStage) {
      if (typeof this.imaAdTraffickingParameters === 'object') {
        return this.imaAdTraffickingParameters;
      }
    }
    return {};
  }

  /** 
   * @type {() => string} 
   */
  get adTraffickingParametersString() {
    if (this.adOnStage) {
      return this.imaAdTraffickingParametersString;
    }
    return '';
  }

  /** 
   * @type {() => string[]} 
   */
  get adWrapperAdIds() {
    if (this.adOnStage) {
      if (Array.isArray(this.imaAdWrapperAdIds)) {
        return this.imaAdWrapperAdIds;
      }
    }
    return [''];
  }

  /** 
   * @type {() => string[]} 
   */
  get adWrapperAdSystems() {
    if (this.adOnStage) {
      if (Array.isArray(this.imaAdWrapperAdSystems)) {
        return this.imaAdWrapperAdSystems;
      }
    }
    return [''];
  }

  /** 
   * @type {() => string[]} 
   */
  get adWrapperCreativeIds() {
    if (this.adOnStage) {
      if (Array.isArray(this.imaAdWrapperCreativeIds)) {
        return this.imaAdWrapperCreativeIds;
      }
    }
    return [''];
  }

  /********************************* Video ads API - rmp-vast specific *********************************/
  /** 
   * @type {() => object} 
   */
  getCompanionAd(index) {
    if (this.rmpVast) {
      return this.rmpVast.getCompanionAd(index);
    }
    return {};
  }

  /******************************************* API ANALYTICS *******************************************/
  /** 
   * @type {() => number} 
   */
  get timeViewed() {
    return this.analyticsSecondsPlayed * 1000;
  }

  /** 
   * @type {() => number} 
   */
  get percentViewed() {
    const nowDuration = this.duration;
    if (this.isVod && nowDuration > -1) {
      return FW.precisionRound((this.analyticsSecondsPlayed * 1000 * 100) / nowDuration, 2);
    }
    return -1;
  }

  /** 
   * @type {() => number} 
   */
  get currentTimeInPercent() {
    const nowDuration = this.duration;
    const nowCurrentTime = this.currentTime;
    if (this.isVod && nowCurrentTime > -1 && nowDuration > -1) {
      return ((nowCurrentTime * 100) / nowDuration);
    }
    return -1;
  }

  /** 
   * @type {() => number} 
   */
  get timeReady() {
    if (this.rmpReadyManagement) {
      return this.rmpReadyManagement.timeReady;
    }
    return -1;
  }

  /** 
   * @type {() => number} 
   */
  get startUpTime() {
    if (this.rmpPlaybackManagement) {
      return this.rmpPlaybackManagement.startUpTime;
    }
    return -1;
  }

  /******************************************* Google Analytics API *******************************************/
  /** 
   * @type {() => object} 
   */
  get gaEventParametersConfiguration() {
    return this.gaEventParameters;
  }

  /** 
   * @type {(newGAEventParameters: string) => void} 
   */
  set gaEventParametersConfiguration(newGAEventParameters) {
    if (!FW.isEmptyObject(newGAEventParameters)) {
      this.gaEventParameters = newGAEventParameters;
    }
  }

  /***************************************** Bitmovin Analytics API *****************************************/
  /** 
   * @typedef {object} BitmovinAnalyticsConfig
   * @property {string} key
   * @property {string} videoId
   * @property {string} title
   * @return {BitmovinAnalyticsConfig}
   */
  get bitmovinAnalyticsConfiguration() {
    return this.bitmovinAnalyticsConfig;
  }

  /** 
   * @type {(config: BitmovinAnalyticsConfig) => void} 
   */
  set bitmovinAnalyticsConfiguration(config) {
    this.bitmovinAnalyticsConfig = config;
  }

  /******************************************* Floating API *******************************************/
  /** 
   * @type {() => boolean} 
   */
  get floatingStatus() {
    if (this.rmpFloating && !this.rmpFloating.isAnchored) {
      return true;
    }
    return false;
  }

  /** 
   * @type {(newFloating: boolean) => void} 
   */
  set floatingStatus(newFloating) {
    if (typeof newFloating === 'boolean' && this.rmpFloating) {
      if (newFloating) {
        if (this.rmpFloating.isAnchored) {
          this.rmpFloating.detachPlayer();
        }
      } else {
        if (!this.rmpFloating.isAnchored) {
          this.rmpFloating.attachPlayer();
        }
      }
    }
  }

  /******************************************* Audio API *******************************************/
  /** 
   * returns the list of all audio tracks currently available to the player
   * @typedef {object} AudioTrack
   * @property {boolean} active
   * @property {number} id
   * @property {string} language
   * @return {AudioTrack[]}
   */
  get audioTracks() {

    const defaultReturnValue = [{
      active: false,
      id: -1,
      language: ''
    }];

    if (!this.playerReady) {
      this.rmpUtils.warning(1004);
      return defaultReturnValue;
    }

    const filteredAudioTracks = [];
    if (this.readingHls && this.dom.video.audioTracks && this.dom.video.audioTracks.length > 1) {
      for (let i = 0; i < this.dom.video.audioTracks.length; i++) {
        const audioTrack = this.dom.video.audioTracks[i];
        if (audioTrack.enabled) {
          filteredAudioTracks.push({ language: audioTrack.language, active: true, id: i });
        } else {
          filteredAudioTracks.push({ language: audioTrack.language, active: false, id: i });
        }
      }
    } else if (this.hlsJS && this.hlsJS.audioTracks && this.hlsJS.audioTracks.length > 0) {
      const activeAudio = this.hlsJS.audioTrack;
      this.hlsJS.audioTracks.forEach((element, index) => {
        if (element.id === activeAudio) {
          filteredAudioTracks.push({
            language: element.lang,
            active: true,
            id: index,
            channelsCount: parseInt(element.channels)
          });
        } else {
          filteredAudioTracks.push({
            language: element.lang,
            active: false,
            id: index,
            channelsCount: parseInt(element.channels)
          });
        }
      });
    } else if (this.shakaPlayer) {

      const audioTracks = this.shakaPlayer.getAudioTracks();
      audioTracks.forEach((audioTrack, index) => {
        filteredAudioTracks.push({
          language: audioTrack.language,
          active: audioTrack.active,
          id: index,
          label: audioTrack.label,
          accessibility: audioTrack.accessibilityPurpose,
          channelsCount: audioTrack.channelsCount
        });
      });

    }
    if (filteredAudioTracks.length > 0) {
      return filteredAudioTracks;
    }

    return defaultReturnValue;
  }

  /** 
   * returns the id of the current audio track 
   * @type {() => number} 
   */
  get audioTrack() {
    const currentAudioTracks = this.audioTracks;
    const activeAudioTrack = currentAudioTracks.find(audioTrack => {
      return audioTrack.active;
    });
    if (activeAudioTrack) {
      return activeAudioTrack.id;
    }
    return -1;
  }

  /** 
   * sets the player audio track based on id parameter - no op if id is out of bound
   * @type {(id: number) => void} 
   */
  set audioTrack(id) {
    if (typeof id !== 'number') {
      this.rmpUtils.warning(1003);
      return;
    }
    if (!this.playerReady) {
      this.rmpUtils.warning(1004);
      return;
    }
    const audioTracks = this.audioTracks;
    const audioTracksLength = audioTracks.length;
    if (id > audioTracksLength - 1 || id < 0) {
      this.rmpUtils.warning(1003);
      return;
    }
    if (this.rmpGoogleCast) {
      this.rmpGoogleCast.setCastAudioTrack(id);
    }
    if (this.readingHls && this.dom.video.audioTracks && this.dom.video.audioTracks.length > 1) {
      this.dispatch('audiotrackchanging');
      for (let i = 0; i < this.dom.video.audioTracks.length; i++) {
        if (i === id) {
          this.dom.video.audioTracks[i].enabled = true;
        } else {
          this.dom.video.audioTracks[i].enabled = false;
        }
      }
      this.dispatch('audiotrackchanged');
    } else if (this.hlsJS && this.hlsJS.audioTracks && this.hlsJS.audioTracks.length > 1) {
      this.hlsJS.audioTrack = id;
    } else if (this.readingShaka && this.shakaPlayer && this.rmpShakaPlayer) {
      const newAudioTrack = audioTracks.find(track => {
        return track.id === id;
      });
      if (newAudioTrack) {
        this.dispatch('audiotrackchanging');

        // select audio track and UI and force ABR to true
        const newTrack = this.shakaPlayer.getAudioTracks()[newAudioTrack.id];
        this.shakaPlayer.configure({ abr: { enabled: false } });
        this.shakaPlayer.selectAudioTrack(newTrack);

        this.innerAbrAutoMode = true;
        this.shakaPlayer.configure({ abr: { enabled: true } });
        this.dispatch('abrstatuschanged');

        // empty QUALITY module ...
        if (this.dom.qualityOverlayLevelsArea) {
          const qualitiesToRemove = [];
          const qualities = this.dom.qualityOverlayLevelsArea.querySelectorAll('.rmp-overlay-level');
          if (qualities.length > 0) {
            for (let i = 0, len = qualities.length; i < len; i++) {
              const quality = qualities[i];
              if (quality.classList.contains('rmp-q0')) {
                quality.classList.add('rmp-overlay-level-active');
                continue;
              }
              qualitiesToRemove.push(quality);
            }
            qualitiesToRemove.forEach(element => {
              FW.removeElement(element);
            });
          }
          // ... and refill it with new video renditions
          this.rmpShakaPlayer.setUpVideoRenditions();
        }

        // reset captions if any 
        const textTracks = this.shakaPlayer.getTextTracks();
        if (!this.hasExternalCCFiles &&
          textTracks.length > 0 &&
          this.dom.captionsOverlayLevelsArea &&
          this.rmpCCManagement) {
          this.rmpCCManagement.defaultTextTrackIndex = 0;
          const ccButtons = this.dom.captionsOverlayLevelsArea.querySelectorAll('.rmp-overlay-level');
          if (ccButtons.length > 0) {
            for (let i = 0, len = ccButtons.length; i < len; i++) {
              ccButtons[i].classList.remove('rmp-overlay-level-active');
            }
          }
          const offCC = this.dom.container.querySelector('.captions-off');
          if (offCC) {
            offCC.classList.add('rmp-overlay-level-active');
          }
          this.shakaPlayer.setTextTrackVisibility(false);
        }
        this.dispatch('audiotrackchanged');
      }
    }
    this.rmpModuleManagement.setActiveLevel('audio', id);

  }

  /******************************************* Quality API *******************************************/
  /** 
   * Returns the list of available video renditions currently available to the player
   * @typedef {object} Quality
   * @property {boolean} active
   * @property {number} audioCodec
   * @property {number} bitrate
   * @property {number} height
   * @property {number} id
   * @property {string} videoCodec
   * @property {number} width
   * @return {Quality[]}
   */
  get qualities() {

    const defaultReturnValue = [{
      active: false,
      audioCodec: '',
      bitrate: -1,
      height: -1,
      id: -1,
      videoCodec: '',
      width: -1
    }];

    if (!this.playerReady) {
      this.rmpUtils.warning(1004);
      return defaultReturnValue;
    }

    const currentQualities = [];
    if (this.shakaPlayer && this.rmpShakaPlayer) {
      const variants = this.shakaPlayer.getVariantTracks();
      variants.forEach(variant => {
        const bitrate = {};
        for (let i = 0, len = this.rmpShakaPlayer.shakaVideoRenditions.length; i < len; i++) {
          const id = this.rmpShakaPlayer.shakaVideoRenditions[i];
          if (variant.id === id) {
            bitrate.id = i;
            bitrate.active = false;
            if (variant.active) {
              bitrate.active = true;
            }
            if (variant.bandwidth) {
              bitrate.bandwidth = variant.bandwidth;
            }
            if (variant.width) {
              bitrate.width = variant.width;
            }
            if (variant.height) {
              bitrate.height = variant.height;
            }
            if (variant.videoCodec) {
              bitrate.videoCodec = variant.videoCodec;
            }
            if (variant.audioCodec) {
              bitrate.audioCodec = variant.audioCodec;
            }
            currentQualities.push(bitrate);
            break;
          }
        }
      });
      return currentQualities;
    } else if (this.hlsJS) {
      if (Array.isArray(this.hlsJS.levels) && this.hlsJS.levels.length > 0) {
        const currentLevelIndex = this.hlsJS.currentLevel;
        this.hlsJS.levels.forEach((element, index) => {
          const bitrate = {};
          bitrate.id = index;
          bitrate.active = false;
          if (index === currentLevelIndex) {
            bitrate.active = true;
          }
          if (element.bitrate) {
            bitrate.bitrate = element.bitrate;
          }
          if (element.width) {
            bitrate.width = element.width;
          }
          if (element.height) {
            bitrate.height = element.height;
          }
          if (element.videoCodec) {
            bitrate.videoCodec = element.videoCodec;
          }
          if (element.audioCodec) {
            bitrate.audioCodec = element.audioCodec;
          }
          currentQualities.push(bitrate);
        });
        return currentQualities;
      }
    } else if (this.readingMp4) {
      const currentIndex = this.quality;
      this.src.mp4.forEach((element, id) => {
        let active = false;
        if (id === currentIndex) {
          active = true;
        }
        currentQualities.push({ active, id });
      });
      return currentQualities;
    } else if (this.readingM4a || this.readingMp3 || this.readingOgg) {
      currentQualities.push({ active: true, id: 0 });
      return currentQualities;
    }

    return defaultReturnValue;
  }

  /** 
   * returns the id of the current video rendition 
   * @type {() => number} 
   */
  get quality() {
    if (!this.playerReady) {
      this.rmpUtils.warning(1004);
      return -2;
    }

    if (this.readingMse || this.readingHls) {
      if (this.abrAutoMode) {
        return -1;
      }
      const active = this.qualities.find(quality => {
        return quality.active;
      });
      if (active) {
        return active.id;
      }
    } else if (this.readingMp4) {
      const currentSrc = this.getSrc();
      let index = this.src.mp4.findIndex(src => {
        return src === currentSrc;
      });
      if (index === -1) {
        index = 0;
      }
      return index;
    } else if (this.readingM4a || this.readingMp3 || this.readingOgg) {
      return 0;
    }

    return -2;
  }

  /** 
   * set the video rendition in the player based on input parameter (video track id from qualities or quality getters)
   * set to -1 for auto ABR
   * @type {(index: number) => void} 
   */
  set quality(index) {
    if (typeof index !== 'number') {
      this.rmpUtils.warning(1003);
      return;
    }
    if (!this.playerReady) {
      this.rmpUtils.warning(1004);
      return;
    }
    if (this.linearAdOnStage) {
      this.rmpUtils.warning(1050);
      return;
    }
    const availableQualities = this.qualities;
    if ((index !== -1 && typeof availableQualities[index] === 'undefined') ||
      (this.readingMp4 && index === -1)) {
      this.rmpUtils.warning(1003);
      return;
    }
    if (this.readingMp4) {
      if (this.rmpPDQuality) {
        this.rmpPDQuality.setMp4Bitrate(index);
      }
    } else if (this.shakaPlayer && this.rmpShakaPlayer) {
      if (index === -1) {
        this.innerAbrAutoMode = true;
        this.shakaPlayer.configure({ abr: { enabled: true } });
      } else {
        this.innerAbrAutoMode = false;
        this.shakaPlayer.configure({ abr: { enabled: false } });
        const newTrackId = this.rmpShakaPlayer.shakaVideoRenditions[index];
        const variants = this.shakaPlayer.getVariantTracks();
        const newTrack = variants.find(variant => {
          return variant.id === newTrackId;
        });
        if (newTrack) {
          if (this.manualSwitchingMode === 'instant') {
            this.shakaPlayer.selectVariantTrack(newTrack, true);
          } else if (this.manualSwitchingMode === 'smooth') {
            this.shakaPlayer.selectVariantTrack(newTrack, true, 8);
          } else {
            this.shakaPlayer.selectVariantTrack(newTrack, false);
          }
          this.rmpUtils.fireLevelSwitchEvents();
        }
      }

      // add rmp-abr-active to selected renditions
      if (this.dom.qualityOverlayLevelsArea) {
        const qualities = this.dom.qualityOverlayLevelsArea.querySelectorAll('.rmp-overlay-level');
        if (qualities.length > 0 && qualities[index + 1]) {
          for (let i = 0, len = qualities.length; i < len; i++) {
            qualities[i].classList.remove('rmp-abr-active');
          }
          qualities[index + 1].classList.add('rmp-abr-active');
          this.rmpStreamingHelpers.updateQualityModuleIcon(qualities[index + 1]);
        }
        this.rmpModuleManagement.setActiveLevel('quality', index + 1);
      }

      // fires abrstatuschanged now
      this.dispatch('abrstatuschanged');
    } else if (this.hlsJS) {
      if (index === -1) {
        this.innerAbrAutoMode = true;
        try {
          if (this.manualSwitchingMode === 'instant') {
            this.hlsJS.currentLevel = -1;
          } else if (this.manualSwitchingMode === 'smooth') {
            this.hlsJS.nextLevel = -1;
          } else {
            this.hlsJS.loadLevel = -1;
          }
          this.dispatch('abrstatuschanged');
        } catch (warning) {
          console.warn(warning);
        }
      } else {
        const levels = this.hlsJS.levels;
        if (levels && levels.length && (index >= 0 && index < levels.length)) {
          this.innerAbrAutoMode = false;
          try {
            if (this.manualSwitchingMode === 'instant') {
              this.hlsJS.currentLevel = index;
            } else if (this.manualSwitchingMode === 'smooth') {
              this.hlsJS.nextLevel = index;
            } else {
              this.hlsJS.loadLevel = index;
            }
            this.dispatch('abrstatuschanged');
          } catch (warning) {
            console.warn(warning);
          }
        }
      }
      this.rmpModuleManagement.setActiveLevel('quality', index + 1);
    }

  }

  /** 
   * Getter for adaptive bitrate streaming mode (true === enabled - false === disabled)
   * @type {() => boolean} 
   */
  get abrAutoMode() {
    if (this.readingMse) {
      return this.innerAbrAutoMode;
    } else if (this.readingHls) {
      return true;
    } else if (this.readingMp4 || this.readingM4a || this.readingMp3 || this.readingOgg) {
      return false;
    }
    return false;
  }

  /******************************************* Destroy API *******************************************/
  /** 
   * Method to destroy player instance (free up resources - this does not remove player container from DOM - if you want to remove player container please do so when destroycompleted event fires or destroy resolves). Returns Promise that resolves if successful (rejects otherwise)
   * @return {Promise<>}
   */
  destroy() {
    return new Promise((resolve, reject) => {
      this.destroyResolve = resolve;
      this.destroyReject = reject;

      if (!this.playerReady) {
        this.rmpUtils.warning(1004);
        this.rejectPromise('destroy', this.warningData);
        return;
      }

      if (this.rmpApiDestroy && this.rmpApiDestroy.running) {
        this.rmpUtils.warning(1013);
        this.rejectPromise('destroy', this.warningData);
        return;
      }

      this.rmpApiDestroy = new ApiDestroy(this);
      if (this.changingSrc) {
        this.changingSrc = false;
        Logger.print('api', 'setSrc in progress - cancelling setSrc now');
      }
      Logger.print('api', 'destroy starts');
      window.removeEventListener('online', this.windowOnlineFn);
      window.removeEventListener('offline', this.windowOfflineFn);
      if (window.rmpGlobals.fullscreenProxyEnabled) {
        document.removeEventListener('webkitfullscreenchange', FW.proxyFullscreenEvents);
        document.removeEventListener('mozfullscreenchange', FW.proxyFullscreenEvents);
        document.removeEventListener('MSFullscreenChange', FW.proxyFullscreenEvents);
      }
      this.rmpSizeManagement.destroy();
      this.rmpAccessible.destroy();
      this.rmpLogsManagement.destroy();
      // reset events
      if (this.rmpGoogleAnalytics) {
        this.rmpGoogleAnalytics.destroy();
      }
      // MUX 
      if (this.rmpMux) {
        this.rmpMux.destroy();
      }
      // destroy
      if (this.isInFullscreen) {
        this.proceedApiDestroy = false;
        this.one('exitfullscreen', () => {
          this.proceedApiDestroy = true;
        });
        this.setFullscreen(false).catch(warning => {
          this.rmpUtils.warning(1017, warning);
        });
      }
      if (this.proceedApiDestroy) {
        this.rmpApiDestroy.clear();
      } else {
        const destroyInterval = window.setInterval(() => {
          if (this.proceedApiDestroy) {
            window.clearInterval(destroyInterval);
            this.rmpApiDestroy.clear();
          }
        }, 40);
      }
    });
  }

  /******************************************* METADATA API *******************************************/
  /** 
   * Read the current set of content metadata available to the player
   * @type {() => ContentMetadata} 
   */
  get contentMetadataConfiguration() {
    if (!FW.isEmptyObject(this.contentMetadata)) {
      return this.contentMetadata;
    }
    return DefaultsParam.contentMetadata;
  }

  /** 
   * Update the current set of content metadata available to the player
   * @type {(metadata: ContentMetadata) => void} 
   */
  set contentMetadataConfiguration(metadata) {
    if (metadata && metadata.noFilter) {
      this.contentMetadata = metadata;
    } else {
      this.contentMetadata = FW.filterContentMetadata(metadata);
    }
    this.rmpPosterManagement.filterPoster();
    // set content title
    if (typeof this.contentMetadata.title === 'string' && this.contentMetadata.title !== '') {
      // append title if it does not exist yet
      if (!this.dom.contentTitle) {
        this.rmpUIManagement.appendContentTitle();
      }
      // update aria label
      this.dom.container.setAttribute('aria-label', this.contentMetadata.title);
      // update title
      this.dom.contentTitle.innerHTML = this.contentMetadata.title;
      // update videoTitle
      if (this.isMobile && this.contentMetadata.title) {
        this.dom.video.setAttribute('title', this.contentMetadata.title);
      }
    }
    // set content description
    if (typeof this.contentMetadata.description === 'string' && this.contentMetadata.description !== '') {
      if (this.dom.description) {
        this.dom.description.innerHTML = this.contentMetadata.description;
        if (this.dom.descriptionLabel) {
          FW.setText(this.dom.descriptionLabel, this.contentMetadata.description);
        }
      } else {
        this.rmpUIManagement.appendContentDescription();
      }
    }
    if (this.contentMetadata.poster[0]) {
      this.poster = this.contentMetadata.poster;
    }
    // animated poster
    if (this.contentMetadata.animatedPoster) {
      if (this.dom.animatedPosterImg) {
        if (this.dom.animatedPosterImg.src !== this.contentMetadata.animatedPoster) {
          this.dom.animatedPosterImg.src = this.contentMetadata.animatedPoster;
        }
      } else {
        this.rmpPosterManagement.initAnimatedPoster();
      }
    }
    if (this.rmpMediaSessionMetadata) {
      // for playlist/related -> add artwork from poster
      if (metadata && metadata.noFilter) {
        if (this.contentMetadata.poster.length > 0) {
          for (let i = 0, len = this.contentMetadata.poster.length; i < len; i++) {
            this.contentMetadata.artwork[i] = {};
            this.contentMetadata.artwork[i].src = this.contentMetadata.poster[i];
          }
        }
      }
      this.rmpMediaSessionMetadata.updateMetadata();
    }
  }

  /******************************************* Airplay API *******************************************/
  /** 
   * Method to show AirPlay receiver picker to the viewer (no-op if AirPlay is not available to the player)
   * @type {() => void} 
   */
  showAirplayPlaybackTargetPicker() {
    if (this.rmpAirPlay) {
      this.rmpAirPlay.onAirPlayClick();
    }
  }

  /******************************************* Offline API *******************************************/
  /** 
   * Request the player to query the current list of downloaded content. Returns Promise that resolves to an Array of DownloadedItem object if listDownloadedContent is successful or rejects to a ErrorData object (warning).
   * @typedef {object} DownloadedItem
   * @property {object} appMetadata
   * @property {number} duration
   * @property {number} expiration
   * @property {string} offlineUri
   * @property {string} originalManifestUri
   * @property {number} size
   * @return {Promise<DownloadedItem[]>}
   */
  listDownloadedContent() {
    return new Promise((resolve, reject) => {
      if (!this.environment.hasDownloadSupport) {
        this.rmpUtils.warning(3007);
        reject(this.warningData);
        return;
      }
      if (!window.rmpGlobals.rmpStorage) {
        this.rmpUtils.warning(3001);
        reject(this.warningData);
        return;
      }

      // Begins DEBUG
      if (DEBUG) {
        if (navigator.storage && navigator.storage.estimate) {
          navigator.storage.estimate().then(({ usage, quota }) => {
            const percentUsed = Math.round(usage / quota * 100);
            const usageInMib = Math.round(usage / (1024 * 1024));
            const quotaInMib = Math.round(quota / (1024 * 1024));
            Logger.print('offline', usageInMib + ' out of ' + quotaInMib + ' MiB used (' + percentUsed + '%)');
          }).catch(warning => {
            this.rmpUtils.warning(3006, warning);
          });
        } else if (navigator.webkitTemporaryStorage && navigator.webkitTemporaryStorage.queryUsageAndQuota) {
          navigator.webkitTemporaryStorage.queryUsageAndQuota(
            (usedBytes, grantedBytes) => {
              Logger.print('offline',
                'webkitTemporaryStorage - we are using ' + usedBytes + ' of ' + grantedBytes + ' bytes available');
            },
            error => {
              this.rmpUtils.warning(3006, error);
            });
        }
        if (navigator.storage && navigator.storage.persist) {
          navigator.storage.persisted().then(persistent => {
            if (persistent) {
              Logger.print('offline', '"persistent" storage - will not be cleared except by explicit user action.');
            } else {
              Logger.print('offline', '"best effort" storage - may be cleared by the UA under storage pressure.');
            }
          });
        }
      }
      // Ends DEBUG

      window.rmpGlobals.rmpStorage.list().then(shakaOfflineList => {
        resolve(shakaOfflineList);
      }).catch(warning => {
        this.rmpUtils.warning(3003, warning);
        reject(this.warningData);
      });
    });
  }

  /** 
   * Request the player to start downloading content for offline storage. Returns Promise that resolves if download has been successful or rejects to a ErrorData object (warning).
   * @return {Promise<>}
   */
  download() {
    return new Promise((resolve, reject) => {
      if (!this.environment.hasDownloadSupport) {
        this.rmpUtils.warning(3007);
        reject(this.warningData);
        return;
      }
      if (!window.rmpGlobals.rmpStorage || !this.rmpShakaPlayer) {
        this.rmpUtils.warning(3001);
        reject(this.warningData);
        return;
      }
      this.rmpShakaPlayer.shakaOfflineDownloadProgress = 0;
      this.dispatch('downloadstarted');
      this.shakaOfflineStoreOperation = window.rmpGlobals.rmpStorage.store(this.startingSrcUri);
      this.shakaOfflineStoreOperation.promise.then(() => {
        this.rmpShakaPlayer.offlineDownloadProgress(null, 1);
        this.shakaOfflineStoreOperation = null;
        this.dispatch('downloadcompleted');
        resolve();
      }).catch(warning => {
        this.rmpShakaPlayer.offlineDownloadProgress(null, -1);
        this.shakaOfflineStoreOperation = null;
        this.rmpUtils.warning(3002, warning);
        reject(this.warningData);
      });
    });
  }

  /** 
   * Request the player to abort downloading content for offline storage. Returns Promise that resolves if download has been successfully aborted or rejects to a ErrorData object (warning).
   * @return {Promise<>}
   */
  abortDownload() {
    return new Promise((resolve, reject) => {
      if (this.shakaOfflineStoreOperation) {
        Logger.print('offline', 'abort download starts');
        this.shakaOfflineStoreOperation.abort().then(() => {
          this.dispatch('abortdownloadcompleted');
          resolve();
        }).catch(warning => {
          this.rmpUtils.warning(3008, warning);
          reject(this.warningData);
        });
      }
    });
  }

  /** 
   * Getter - must be queried when downloadprogress API event fires and will return a Number between 0 and 1 representing the current progress for the current download. Returns -1 if this value is not available.
   * @type {() => number} 
   */
  get downloadProgress() {
    if (this.rmpShakaPlayer) {
      return this.rmpShakaPlayer.shakaOfflineDownloadProgress;
    }
    return -1;
  }

  /** 
   * Request the player to load a previously downloaded content. This method takes a DownloadedItem object as an input. A list of DownloadedItem objects can be obtained with listDownloadedContent method. Returns Promise that resolves if loadDownload is successful or rejects to a ErrorData object.
   * @param {DownloadedItem} content - the offline item to load (list of DownloadedItem objects can be obtained with listDownloadedContent method)
   * @return {Promise<>}
   */
  loadDownload(content) {
    return new Promise((resolve, reject) => {
      if (!this.environment.hasDownloadSupport) {
        this.rmpUtils.warning(3007);
        reject(this.warningData);
        return;
      }
      if (!window.rmpGlobals.rmpStorage) {
        this.rmpUtils.warning(3001);
        reject(this.warningData);
        return;
      }
      if (!content || !content.offlineUri) {
        this.rmpUtils.warning(3005);
        reject(this.warningData);
        return;
      }
      this.setSrc({ [this.streamType]: content.offlineUri }).then(() => {
        resolve();
      }).catch(warning => {
        this.rmpUtils.warning(1025, warning);
        reject(this.warningData);
      });
    });
  }

  /** 
   * Request the player to remove a DownloadedItem from offline storage. This method takes a DownloadedItem object as an input. A list of DownloadedItem objects can be obtained with listDownloadedContent method. Returns Promise that resolves if removeDownload is successful or rejects to a ErrorData object (warning).
   * @param {DownloadedItem} content - the offline item to remove (list of DownloadedItem objects can be obtained with listDownloadedContent method)
   * @return {Promise<>}
   */
  removeDownload(content) {
    return new Promise((resolve, reject) => {
      if (!this.environment.hasDownloadSupport) {
        this.rmpUtils.warning(3007);
        reject(this.warningData);
        return;
      }
      if (!window.rmpGlobals.rmpStorage) {
        this.rmpUtils.warning(3001);
        reject(this.warningData);
        return;
      }
      window.rmpGlobals.rmpStorage.remove(content.offlineUri).then(() => {
        this.dispatch('downloadremoved');
        resolve();
      }).catch(warning => {
        this.rmpUtils.warning(3004, warning);
        reject(this.warningData);
      });
    });
  }

  /******************************************* Playlist API *******************************************/
  /** 
   * Returns the index of the current playlist item loaded in the player
   * @type {() => number} 
   */
  getPlaylistItem() {
    if (this.rmpPlaylist) {
      return this.rmpPlaylist.currentPlaylistIndex;
    }
    return 0;
  }

  /** 
   * Load a new playlist item into the player. Returns Promise that resolves if setPlaylistItem is successful or rejects if an error occurs while trying to load new playlist item
   * @param {number} index - the new playlist item to load
   * @return {Promise<>} 
   */
  setPlaylistItem(index) {
    return new Promise((resolve, reject) => {

      if (typeof index !== 'number' || index < 0) {
        this.rmpUtils.warning(1003);
        reject(this.warningData);
        return;
      }
      if (!this.rmpPlaylist) {
        this.rmpUtils.warning(7001);
        reject(this.warningData);
        return;
      }

      let _onSrcChanged, _onError;
      _onSrcChanged = () => {
        this.off('error', _onError);
        resolve();
      };
      _onError = () => {
        this.off('srcchanged', _onSrcChanged);
        this.rmpUtils.error(709);
        reject(this.warningData);
      };
      this.one('srcchanged', _onSrcChanged);
      this.one('error', _onError);

      this.rmpPlaylist.loadPlaylistItem(index);

    });
  }

  /** 
   * Returns a list of PlaylistItem currently available to the player
   * @typedef {object} PlaylistItem
   * @property {object} src
   * @property {ContentMetadata} contentMetadata
   * @property {string} [adTagUrl]
   * @property {string[]} [adTagWaterfall]
   * @property {string} [seekBarThumbnailsLoc]
   * @property {TextTrack[]} [ccFiles]
   * @property {string} [chaptersLoc]
   * @property {string} [sharingUrl]
   * @property {string} [sharingCode]
   * @property {Logo} [logo]
   * @return {PlaylistItem[]}
   */
  getPlaylistData() {
    if (this.rmpPlaylist && this.rmpPlaylist.playlistList.length > 0) {
      return this.rmpPlaylist.playlistList;
    }
    return [{}];
  }

  /** 
   * Load a new list of PlaylistItem into the player. Returns Promise that resolves if setPlaylistData is successful or rejects to a ErrorData object.
   * @param {PlaylistItem[]} playlistData - the new list of PlaylistItem to load in the player
   * @return {Promise<>}
   */
  setPlaylistData(playlistData) {
    return new Promise((resolve, reject) => {
      if (this.rmpPlaylist && (Array.isArray(playlistData) && typeof playlistData[0] === 'object')) {
        this.rmpPlaylist.setInternalPlaylistData(playlistData).
          then(() => {
            resolve();
          }).
          catch((error) => {
            this.rmpUtils.error(706, error);
            reject(this.errorData);
          });
      } else {
        this.rmpUtils.error(705);
        reject(this.errorData);
      }
    });
  }

  /** 
   * Returns the URI to the JSON playlist file currently loaded into the player
   * @type {() => string} 
   */
  getPlaylistLoc() {
    if (this.rmpPlaylist) {
      return this.playlistLoc;
    } else {
      return '';
    }
  }

  /** 
   * Load a new list of PlaylistItem into the player. Returns Promise that resolves if setPlaylistLoc is successful or rejects to a ErrorData object.
   * @param {string} playlistLoc - URI to new list of PlaylistItem to load in the player
   * @return {Promise<>}
   */
  setPlaylistLoc(playlistLoc) {
    return new Promise((resolve, reject) => {
      if (this.rmpPlaylist && (typeof playlistLoc === 'string' && playlistLoc !== '')) {
        this.rmpPlaylist.setInternalPlaylistData(playlistLoc).
          then(() => {
            resolve();
          }).
          catch((error) => {
            this.rmpUtils.error(708, error);
            reject(this.errorData);
          });
      } else {
        this.rmpUtils.error(707);
        reject(this.errorData);
      }
    });
  }

  /** 
   * This method sets dynamically the playlistCallback setting. This setting allows for a custom callback (Promise-based) to be run during the playlist item changing mechanism after a new playlist item has been requested (including the first item) but before it actually starts loading playlist data for that item. The playlist item loading process will only resume when the Promise from the custom callback resolves or rejects. This allows for use-cases that requires the playlist item loading process to pause, like header bidding for video ads, to be handled.
   * @type {(callback: function) => void} 
   */
  setPlaylistItemCallback(callback) {
    if (this.rmpPlaylist && typeof callback === 'function') {
      this.playlistCallback = callback;
    }
  }

  /******************************************* Related API *******************************************/
  /** 
   * Returns the index of the current related item loaded in the player
   * @type {() => number} 
   */
  getRelatedItem() {
    if (this.rmpRelated) {
      return this.rmpRelated.currentRelatedIndex;
    }
    return 0;
  }

  /** 
   * Load a new related item with parameter index into the player
   * Returns a Promise that:
   * resolves if new item successfully loads in player 
   * rejects if an error occurs while trying to load new item
   * @param {number} index
   * @return {Promise<>} 
   */
  setRelatedItem(index) {
    return new Promise((resolve, reject) => {

      if (typeof index !== 'number' || index < 0) {
        this.rmpUtils.warning(1003);
        reject(this.warningData);
        return;
      }
      if (!this.rmpRelated) {
        this.rmpUtils.warning(7001);
        reject(this.warningData);
        return;
      }

      let _onSrcChanged, _onError;
      _onSrcChanged = () => {
        this.off('error', _onError);
        resolve();
      };
      _onError = () => {
        this.off('srcchanged', _onSrcChanged);
        this.rmpUtils.error(718);
        reject(this.warningData);
      };
      this.one('srcchanged', _onSrcChanged);
      this.one('error', _onError);

      this.rmpRelated.loadRelatedItem(index);

    });
  }

  /** 
   * Returns a list of RelatedItem currently available to the player
   * @typedef {object} RelatedItem
   * @property {object} src
   * @property {ContentMetadata} contentMetadata
   * @property {string} [adTagUrl]
   * @property {string[]} [adTagWaterfall]
   * @property {string} [seekBarThumbnailsLoc]
   * @property {TextTrack[]} [ccFiles]
   * @property {string} [chaptersLoc]
   * @property {string} [sharingUrl]
   * @property {string} [sharingCode]
   * @property {Logo} [logo]
   * @return {RelatedItem[]}
   */
  getRelatedData() {
    if (this.rmpRelated && this.rmpRelated.relatedList.length > 0) {
      return this.rmpRelated.relatedList;
    }
    return [{}];
  }

  /** 
   * Load a new list of RelatedItem into the player. This method returns a Promise that resolves if player has successfully updated the list of RelatedItem or rejects to a ErrorData object if not.
   * @param {RelatedItem[]} relatedData
   * @return {Promise<>}
   */
  setRelatedData(relatedData) {
    return new Promise((resolve, reject) => {
      if (this.rmpRelated && (Array.isArray(relatedData) && typeof relatedData[0] === 'object')) {
        this.rmpRelated.setInternalRelatedData(relatedData).
          then(() => {
            resolve();
          }).
          catch((error) => {
            this.rmpUtils.error(715, error);
            reject(this.errorData);
          });
      } else {
        this.rmpUtils.error(714);
        reject(this.errorData);
      }
    });
  }

  /** 
   * Returns the URI to the JSON related file currently loaded into the player
   * @type {() => string} 
   */
  getRelatedLoc() {
    if (this.rmpRelated) {
      return this.relatedLoc;
    } else {
      return '';
    }
  }

  /** 
   * Load a new list of RelatedItem into the player. This method returns a Promise that resolves if player has successfully updated the list of RelatedItem or rejects to a ErrorData object if not.
   * @param {string} relatedLoc
   * @return {Promise<>}
   */
  setRelatedLoc(relatedLoc) {
    return new Promise((resolve, reject) => {
      if (this.rmpRelated && (typeof relatedLoc === 'string' && relatedLoc !== '')) {
        this.rmpRelated.setInternalRelatedData(relatedLoc).
          then(() => {
            resolve();
          }).
          catch((error) => {
            this.rmpUtils.error(717, error);
            reject(this.errorData);
          });
      } else {
        this.rmpUtils.error(716);
        reject(this.errorData);
      }
    });
  }

  /** 
   * This method sets dynamically the relatedCallback setting. This setting allows for a custom callback (Promise-based) to be run during the related item changing mechanism after a new related item has been requested (including the first item) but before it actually starts loading related data for that item. The related item loading process will only resume when the Promise from the custom callback resolves or rejects. This allows for use-cases that requires the related item loading process to pause, like header bidding for video ads, to be handled.
   * @type {(callback: function) => void} 
   */
  setRelatedItemCallback(callback) {
    if (this.rmpRelated && typeof callback === 'function') {
      this.relatedCallback = callback;
    }
  }

  /******************************************* SRC API *******************************************/
  #resumeSetSrc(src, pdIndex) {
    // src changing
    this.changingSrc = true;
    this.rmpApiSrc = new ApiSrc(this);
    this.rmpApiSrc.init(src, pdIndex);
  }

  /** 
   * change player content source based on input
   * return Promise that resolves if successful (rejects otherwise)
   * @param {Src} src
   * @param {number} [pdIndex]
   * @return {Promise<>}
   */
  setSrc(src, pdIndex) {
    return new Promise((resolve, reject) => {

      this.setSrcResolve = resolve;
      this.setSrcReject = reject;

      if (typeof src !== 'object') {
        this.rmpUtils.warning(1003);
        this.rejectPromise('setSrc', this.warningData);
        return;
      }
      if (!this.playerReady) {
        this.rmpUtils.warning(1004);
        this.rejectPromise('setSrc', this.warningData);
        return;
      }
      if (this.readingFps || this.readingOutstream) {
        this.rmpUtils.warning(12006);
        this.rejectPromise('setSrc', this.warningData);
        return;
      }
      if (this.changingSrc) {
        this.rmpUtils.warning(12000);
        this.rejectPromise('setSrc', this.warningData);
        return;
      }

      const validSrcType = ['hls', 'dash', 'mp4', 'webm', 'm4a', 'mp3', 'ogg', 'fps'];
      const foundSrc = validSrcType.find(srcType => {
        return typeof src[srcType] !== 'undefined';
      });
      if (typeof foundSrc === 'undefined') {
        this.rmpUtils.warning(1003);
        this.rejectPromise('setSrc', this.warningData);
        return;
      }

      if (this.streamType === 'nosupport') {
        this.rmpUtils.warning(12001);
        this.rejectPromise('setSrc', this.warningData);
        return;
      }

      // setSrc after ready but before hlsjsinstancecreated or shakainstancecreated
      if (this.readingMse && this.msePreloadRequested && !this.hlsjsInstanceCreated && !this.shakaInstanceCreated) {
        Logger.print('api', 'hls.js or Shaka player instance is still being created - wait for creation before setSrc');
        if (this.readingHlsJS) {
          this.one('hlsjsinstancecreated', () => {
            Logger.print('api', 'hls.js created - continue setSrc');
            this.#resumeSetSrc(src, pdIndex);
          });
        } else {
          this.one('shakainstancecreated', () => {
            Logger.print('api', 'Shaka player created - continue setSrc');
            this.#resumeSetSrc(src, pdIndex);
          });
        }
      } else {
        this.#resumeSetSrc(src, pdIndex);
      }
    });
  }

  /** 
   * @type {() => string} 
   */
  getSrc() {
    return this.startingSrcUri;
  }

  /** 
   * @type {() => boolean} 
   */
  isChangingSrc() {
    return this.changingSrc;
  }

  /******************************************* Shaka DRM API *******************************************/
  /** 
   * @type {() => function} 
   */
  get shakaCustomRequestFilterConfiguration() {
    return this.shakaCustomRequestFilter;
  }

  /** 
   * @type {(shakaCustomRequestFilter: function) => void} 
   */
  set shakaCustomRequestFilterConfiguration(shakaCustomRequestFilter) {
    if (typeof shakaCustomRequestFilter === 'function') {
      this.shakaCustomRequestFilter = shakaCustomRequestFilter;
    }
  }

  /** 
   * @type {() => object} 
   */
  get shakaRequestFilterConfiguration() {
    return this.shakaRequestConfiguration;
  }

  /** 
   * @type {(shakaRequestConfiguration: object) => void} 
   */
  set shakaRequestFilterConfiguration(shakaRequestConfiguration) {
    if (typeof shakaRequestConfiguration === 'object') {
      this.shakaRequestConfiguration = shakaRequestConfiguration;
    }
  }

  /** 
   * @type {() => ShakaDrm} 
   */
  get shakaDrmConfiguration() {
    return this.shakaDrm;
  }

  /** 
   * @type {(shakaDrm: ShakaDrm) => void} 
   */
  set shakaDrmConfiguration(shakaDrm) {
    if (!FW.isEmptyObject(shakaDrm)) {
      this.shakaDrm = shakaDrm;
    }
  }

  /******************************************* Novage P2P API *******************************************/
  get shakaP2PEngine() {
    if (this.rmpShakaPlayer) {
      return this.rmpShakaPlayer.shakaP2PEngine;
    }
    return null;
  }
}
